import React, { useState, useEffect, forwardRef, useMemo, useRef, useCallback } from 'react';
import {
    DataGridPremium,
    useGridApiRef,
    useGridApiContext,
    useGridSelector,
    gridPageCountSelector,
    gridPaginationModelSelector,
    gridFilteredTopLevelRowCountSelector,
    gridPaginationRowRangeSelector,
    gridRowCountSelector,
    GridPreferencePanelsValue,
    gridVisibleColumnFieldsSelector,
    gridFilteredSortedRowIdsSelector,
    getGridStringOperators
} from '@mui/x-data-grid-premium';
import {
  Button,
  Box,
  Tabs,
  Tab,
  Stack,
  IconButton,
  FormControl,
  Checkbox,
  TextField,
    InputAdornment,
    Pagination,
    Typography,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    RadioGroup,
    FormControlLabel,
    Radio,
    Chip
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';

import {
    ValidationUtility,
    CustomAutoComplete,
    TextFieldWithIconButton,
    SessionManager,
    sessionService,
    ApiManager,
    DeterminePermissions,
    LoggerUtility,
    NotificationDialog,
    WhiteRefreshIcon,
    PageLoader,
    SharedService,
    UtilityLibrary,
    ActionTakenDetails
} from '../ComponentsLibrary';

import AlertNotification from '../AlertNotification';

// Custom filter input: real checkboxes that stay open across multiple selections (unlike MUI's default tag-based "is any of" input)
const CheckboxSetFilterInput = ({ item, applyValue, apiRef }) => {
    const selectedValues = Array.isArray(item?.value)
        ? item.value.map((value) => String(value))
        : [];

    const options = useMemo(() => {
        if (!item?.field || !apiRef?.current?.getRowModels) {
            return [];
        }

        const rowModels = apiRef.current.getRowModels();
        const uniqueValues = new Set();

        rowModels.forEach((row) => {
            const rawValue = row?.[item.field];
            if (rawValue === undefined || rawValue === null || rawValue === '') {
                return;
            }

            uniqueValues.add(String(rawValue));
        });

        return Array.from(uniqueValues).sort((a, b) =>
            a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
        );
    }, [apiRef, item?.field]);

    const handleToggle = useCallback((value) => {
        const stringValue = String(value);
        const hasValue = selectedValues.includes(stringValue);
        const nextValues = hasValue
            ? selectedValues.filter((existing) => existing !== stringValue)
            : [...selectedValues, stringValue];

        applyValue({
            ...item,
            value: nextValues
        });
    }, [applyValue, item, selectedValues]);

    if (!item?.field) {
        return null;
    }

    return (
        <Box sx={{ minWidth: 240, maxHeight: 260, overflowY: 'auto', p: 0.5 }}>
            {options.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#667085', px: 1, py: 0.5 }}>
                    No values available
                </Typography>
            ) : (
                options.map((option) => (
                    <Box
                        key={`${item.field}-${option}`}
                        sx={{ display: 'flex', alignItems: 'center', px: 0.5 }}
                    >
                        <Checkbox
                            size="small"
                            checked={selectedValues.includes(option)}
                            onChange={() => handleToggle(option)}
                        />
                        <Typography variant="body2">{option}</Typography>
                    </Box>
                ))
            )}
        </Box>
    );
};

const CompletedGridFooter = () => {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);
    const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
    const filteredRowCount = useGridSelector(apiRef, gridFilteredTopLevelRowCountSelector);
    const rowRange = useGridSelector(apiRef, gridPaginationRowRangeSelector);
    const totalRowCount = useGridSelector(apiRef, gridRowCountSelector);


    const currentPage = paginationModel?.page || 0;
    const fromIndex = filteredRowCount === 0
        ? 0
        : ((rowRange?.firstRowIndex ?? (currentPage * (paginationModel?.pageSize || 0))) + 1);
    const toIndex = filteredRowCount === 0
        ? 0
        : ((rowRange?.lastRowIndex ?? (Math.min((currentPage + 1) * (paginationModel?.pageSize || 0), filteredRowCount) - 1)) + 1);
    const showingCount = filteredRowCount === 0 ? 0 : Math.max(toIndex - fromIndex + 1, 0);

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                px: 2,
                py: 1,
                borderTop: '1px solid #e0e0e0',
                backgroundColor: '#fff',
                minHeight: 52,
                gap: 2,
                flexWrap: 'wrap'
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: '#344054', fontWeight: 500 }}>
                        Rows per page:
                    </Typography>
                    <select
                        value={paginationModel?.pageSize || 1000}
                        onChange={(event) => {
                            const nextPageSize = Number(event.target.value) || 1000;
                            apiRef.current.setPageSize(nextPageSize);
                            apiRef.current.setPage(0);
                        }}
                        style={{
                            border: '1px solid #d0d5dd',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#344054',
                            backgroundColor: '#fff'
                        }}
                    >
                        {[1000, 2000, 3000, 4000, 5000].map((pageSizeOption) => (
                            <option key={pageSizeOption} value={pageSizeOption}>{pageSizeOption}</option>
                        ))}
                    </select>
                </Box>
                <Typography variant="body2" sx={{ color: '#344054', fontWeight: 500 }}>
                    {`Total Count: ${totalRowCount} | Filtered records ${fromIndex}-${toIndex} (${showingCount} showing) of ${filteredRowCount}`}
                </Typography>
                <Pagination
                    count={Math.max(pageCount, 1)}
                    page={currentPage + 1}
                    onChange={(event, value) => apiRef.current.setPage(value - 1)}
                    showFirstButton
                    showLastButton
                    color="primary"
                    size="small"
                    disabled={filteredRowCount === 0}
                    sx={{
                        '& .MuiPaginationItem-root': {
                            color: '#344054',
                            fontWeight: 500
                        },
                        '& .MuiPaginationItem-root.Mui-selected': {
                            color: '#ffffff'
                        }
                    }}
                />
            </Box>
        </Box>
    );
};

const formatDateMMDDYYYY = (value) => {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};

const formatDateMMDDYYYYForSite = (value, timeZone) => {
    if (!value) return '';

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-');
        return `${month}/${day}/${year}`;
    }

    const dateKey = UtilityLibrary.getDateKeyForTimeZone(timeZone, value);
    if (!dateKey) return '';

    const [year, month, day] = dateKey.split('-');
    return `${month}/${day}/${year}`;
};

const formatDateMMDDYYYYWithTime = (value, timeZone) => {
    if (!value) return '';
    return UtilityLibrary.formatDateTimeForTimeZone(timeZone, value);
};

const QueueCompleted = forwardRef((props, ref) => {

    const errorFormName = "Completed - Workflow";

    const getTodayDate = () => new Date().toISOString().split('T')[0];

    const loginUserInfo = sessionService.getUser();
    const userRoleID = loginUserInfo?.roleID;
    const isRestrictedRole = userRoleID === 9 || userRoleID === 12;
    const isPersonalOnly = [3, 4, 7, 8, 10, 11].includes(userRoleID);

    const initialFormData = useMemo(() => ({
        hasValidCaseData: true,
        hasValidDateData: true,
        startDateTxt: getTodayDate(),
        endDateTxt: getTodayDate(),
        caseSearchTxt: "",
        workflowSelect: [],
        queueSelect: [],
        siteSelect: [],
        userSelect: [],
    }), []);

    const defaultCaseNumberIconsDisabled = useMemo(() => ({ search: true, copy: true, clear: true }), []);
    const initialTabFormState = useMemo(() => ({
        0: { ...initialFormData },
        1: { ...initialFormData },
        2: { ...initialFormData }
    }), [initialFormData]);
    const initialTabCaseNumberIconsState = useMemo(() => ({
        0: { ...defaultCaseNumberIconsDisabled },
        1: { ...defaultCaseNumberIconsDisabled },
        2: { ...defaultCaseNumberIconsDisabled }
    }), [defaultCaseNumberIconsDisabled]);
    const initialTabIncludeSystemActionsState = useMemo(() => ({ 0: false, 1: false, 2: false }), []);
    const initialTabGridFilterState = useMemo(() => ({
        0: { items: [] },
        1: { items: [] },
        2: { items: [] }
    }), []);

    const [searchFormDataByTab, setSearchFormDataByTab] = useState(initialTabFormState);

    const [activeTab, setActiveTab] = useState(0);
    const [hasSearchedByTab, setHasSearchedByTab] = useState({ 0: false, 1: false, 2: false });
    const hasSearched = hasSearchedByTab[activeTab] ?? false;
    const setHasSearched = (value) => setHasSearchedByTab((prev) => ({ ...prev, [activeTab]: value }));
    
    const [selectedQARows, setSelectedQARows] = useState([]);

    const handleQARowSelection = (rowId, isSelected) => {
        if (isSelected) {
            setSelectedQARows(prev => prev.includes(rowId) ? prev : [...prev, rowId]);
        } else {
            setSelectedQARows(prev => prev.filter(id => id !== rowId));
        }
    };

    const handleQASelectAll = (event) => {
        if (event.target.checked) {
            setSelectedQARows(resultsCompleted.map(row => row.id));
        } else {
            setSelectedQARows([]);
        }
    };

    const caseSearchRefs = useRef({});
    const [caseNumberIconsDisabledByTab, setCaseNumberIconsDisabledByTab] = useState(initialTabCaseNumberIconsState);

    const [isPageLoading, setIsPageLoading] = useState(false);

    const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
    const [notificationDialogContent, setNotificationDialogContent] = useState([]);
    const [timerNotificationDialogClose] = useState(5000);
    const [timerAutoCloseNotificationDialog] = useState(false);
    // Each tab keeps its own DataGrid apiRef - sharing a single apiRef across the 3 always-mounted grids
    // causes exportState/restoreState (column widths) to silently target the wrong tab's grid instance.
    const workflowGridApiRef = useGridApiRef();
    const qaGridApiRef = useGridApiRef();
    const varianceGridApiRef = useGridApiRef();
    const gridApiRefsByTabRef = useRef({ 0: workflowGridApiRef, 1: qaGridApiRef, 2: varianceGridApiRef });
    const getGridApiRefForTab = useCallback((tabIndex) => gridApiRefsByTabRef.current[tabIndex] || gridApiRefsByTabRef.current[0], []);
    const completedSearchSeqRef = useRef({ 0: 0, 1: 0, 2: 0 });
    // Per-tab saved column state (order + width) so it survives Export/Filter/Refresh re-renders
    const savedGridStateByTab = useRef({ 0: null, 1: null, 2: null });
    // Per-tab controlled open state for the Workflow/Queue dropdowns, so clicking the clear (X) button doesn't close them
    const [workflowDropdownOpenByTab, setWorkflowDropdownOpenByTab] = useState({});
    const [queueDropdownOpenByTab, setQueueDropdownOpenByTab] = useState({});
    const workflowJustClearedRef = useRef({});
    const queueJustClearedRef = useRef({});
    // Wrapping-div refs (per tab) used to detect genuine outside clicks, since MUI's own onClose
    // doesn't reliably fire after the clear (X) button is used on a controlled-open Autocomplete
    const workflowDropdownContainerRefs = useRef({});
    const queueDropdownContainerRefs = useRef({});
    // Cross-navigation persistence (sessionStorage) - true once the mount-time restore attempt has finished
    const [restoreCompleted, setRestoreCompleted] = useState(false);

    const [qaCheckedRowIds, setQaCheckedRowIds] = useState(new Set());
    const [qaConfirmDialogOpen, setQaConfirmDialogOpen] = useState(false);
    const [qaSuccessDialogOpen, setQaSuccessDialogOpen] = useState(false);
    const [qaSuccessCount, setQaSuccessCount] = useState(0);

    const initialTabResultsState = useMemo(() => ({ 0: [], 1: [], 2: [] }), []);
    const initialTabDensityState = useMemo(() => ({ 0: 'standard', 1: 'standard', 2: 'standard' }), []);
    const initialTabPaginationState = useMemo(() => ({
        0: { pageSize: 1000, page: 0 },
        1: { pageSize: 1000, page: 0 },
        2: { pageSize: 1000, page: 0 }
    }), []);
    const initialTabColumnVisibilityState = useMemo(() => ({
        0: {},
        1: {},
        2: {}
    }), []);
    const [resultsCompletedByTab, setResultsCompletedByTab] = useState(initialTabResultsState);
    const [allCompletedRowsByTab, setAllCompletedRowsByTab] = useState(initialTabResultsState);
    const [includeSystemActionsByTab, setIncludeSystemActionsByTab] = useState(initialTabIncludeSystemActionsState);
    const [paginationModelByTab, setPaginationModelByTab] = useState(initialTabPaginationState);
    const [sortModel, setSortModel] = useState([{ field: 'actionStarted', sort: 'asc' }]);
    const [gridFilterModelByTab, setGridFilterModelByTab] = useState(initialTabGridFilterState);
    const [gridDensityByTab, setGridDensityByTab] = useState(initialTabDensityState);
    const [columnVisibilityModelByTab, setColumnVisibilityModelByTab] = useState(initialTabColumnVisibilityState);
    const [densityMenuAnchorEl, setDensityMenuAnchorEl] = useState(null);
    const [workflowOptions, setWorkflowOptions] = useState([]);
    const [queueOptions, setQueueOptions] = useState([]);
    const [siteOptions, setSiteOptions] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [holidayList, setHolidayList] = useState([]);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exportScope, setExportScope] = useState('all');
    const [fileName, setFileName] = useState('');
    const [includeHeaders, setIncludeHeaders] = useState(true);
    const [includeFilters, setIncludeFilters] = useState(true);
    const [format, setFormat] = useState('excel');
    const [pageSelection, setPageSelection] = useState('current');

    

    const completedGridSx = useMemo(() => ({
        height: '100%',
        minHeight: 0,
        '& .MuiDataGrid-row:hover': {
            cursor: 'pointer'
        },
        '& .MuiDataGrid-main': {
            minHeight: 0
        },
        '& .MuiDataGrid-virtualScroller': {
            overflowX: 'auto',
            overflowY: 'auto'
        }
    }), []);

    const searchFormData = useMemo(() => searchFormDataByTab[activeTab] || initialFormData, [searchFormDataByTab, activeTab, initialFormData]);
    const caseNumberIconsDisabled = useMemo(() => caseNumberIconsDisabledByTab[activeTab] || defaultCaseNumberIconsDisabled, [caseNumberIconsDisabledByTab, activeTab, defaultCaseNumberIconsDisabled]);
    const resultsCompleted = useMemo(() => resultsCompletedByTab[activeTab] || [], [resultsCompletedByTab, activeTab]);
    const allCompletedRows = useMemo(() => allCompletedRowsByTab[activeTab] || [], [allCompletedRowsByTab, activeTab]);
    const includeSystemActions = includeSystemActionsByTab[activeTab] || false;
    const paginationModel = useMemo(() => paginationModelByTab[activeTab] || { pageSize: 1000, page: 0 }, [paginationModelByTab, activeTab]);
    const gridFilterModel = useMemo(() => gridFilterModelByTab[activeTab] || { items: [] }, [gridFilterModelByTab, activeTab]);
    const gridDensity = gridDensityByTab[activeTab] || 'standard';
    const columnVisibilityModel = useMemo(() => columnVisibilityModelByTab[activeTab] || {}, [columnVisibilityModelByTab, activeTab]);
    const activeCaseSearchRef = caseSearchRefs.current[activeTab] || null;
    const isCaseSearchInvalid = Boolean(searchFormData.caseSearchTxt?.trim()) && !searchFormData.hasValidCaseData;

    const completedGridContainerStyle = useMemo(() => ({
        display: 'flex',
        flexDirection: 'column',
        marginTop: '4px',
        width: '100%',
        overflowX: 'auto',
        height: 'calc(100vh - 380px)',
        minHeight: '300px'
    }), []);

    const completedEmptyStateStyle = useMemo(() => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: '8px',
        width: '100%',
        marginTop: '4px',
        minHeight: '220px',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        backgroundColor: '#fff'
    }), []);

    const siteSettings = useMemo(() => {
        const siteSettingsFromSession = SessionManager.GetDataFromSession(SessionManager.SiteSettingsDetailsKey);

        if (!siteSettingsFromSession) {
            return null;
        }

        if (typeof siteSettingsFromSession === 'string') {
            try {
                return JSON.parse(siteSettingsFromSession);
            }
            catch (error) {
                return null;
            }
        }

        return siteSettingsFromSession;
    }, []);

    const loginSiteID = loginUserInfo?.siteID || siteSettings?.siteID || siteSettings?.yTracSiteID || null;
    const loginStateID = loginUserInfo?.stateID || siteSettings?.stateID || siteSettings?.StateID || null;
    const completedDropdownSiteID = loginStateID || loginSiteID || null;
    const masterServerDBSchema = siteSettings?.masterDBSchema || null;
    const caseNumberRegex = useMemo(() => (
        siteSettings?.CaseNumberRegex || { caseRegexValue: '', caseRegexMessage: '' }
    ), [siteSettings]);
    const selectedWorkflowIdsRef = useRef([]);
    const selectedWorkflowIds = useMemo(() => {
        const rawIds = (
            !Array.isArray(searchFormData.workflowSelect) || searchFormData.workflowSelect.length === 0
                ? workflowOptions
                : workflowOptions.filter((option) => searchFormData.workflowSelect.includes(option.value))
        )
            .map((option) => option.id)
            .filter((value) => value !== undefined && value !== null && value !== '')
            .sort();

        const prev = selectedWorkflowIdsRef.current;
        if (rawIds.length === prev.length && rawIds.every((id, i) => id === prev[i])) {
            return prev;
        }
        selectedWorkflowIdsRef.current = rawIds;
        return rawIds;
    }, [searchFormData.workflowSelect, workflowOptions]);

    const gridToolbarButtonSx = useMemo(() => ({
        display: 'flex',
        alignItems: 'center',
        fontSize: '14px',
        fontWeight: '500',
        color: '#5A73EA',
        cursor: 'pointer',
        padding: '6px 8px',
        borderRadius: '4px',
        transition: 'all 0.2s ease',
        minWidth: 'fit-content',
        '&:hover': {
            backgroundColor: 'rgba(90, 115, 234, 0.08)',
            color: '#4A63DA',
            transform: 'translateY(-1px)'
        },
        '&:active': {
            transform: 'translateY(0px)',
            backgroundColor: 'rgba(90, 115, 234, 0.12)'
        },
        '&.Mui-disabled': {
            color: '#a0abc7'
        }
    }), []);

    const setResultsCompleted = useCallback((nextResults) => {
        setResultsCompletedByTab((previous) => ({
            ...previous,
            [activeTab]: typeof nextResults === 'function' ? nextResults(previous[activeTab] || []) : nextResults
        }));
    }, [activeTab]);

    const setAllCompletedRows = useCallback((nextRows) => {
        setAllCompletedRowsByTab((previous) => ({
            ...previous,
            [activeTab]: typeof nextRows === 'function' ? nextRows(previous[activeTab] || []) : nextRows
        }));
    }, [activeTab]);

    const dispatchFormData = useCallback((action) => {
        setSearchFormDataByTab((previous) => ({
            ...previous,
            [activeTab]: {
                ...(previous[activeTab] || initialFormData),
                ...action
            }
        }));
    }, [activeTab, initialFormData]);

    const setCaseNumberIconsDisabled = useCallback((nextValue) => {
        setCaseNumberIconsDisabledByTab((previous) => ({
            ...previous,
            [activeTab]: typeof nextValue === 'function' ? nextValue(previous[activeTab] || defaultCaseNumberIconsDisabled) : nextValue
        }));
    }, [activeTab, defaultCaseNumberIconsDisabled]);

    const setIncludeSystemActions = useCallback((nextValue) => {
        setIncludeSystemActionsByTab((previous) => ({
            ...previous,
            [activeTab]: typeof nextValue === 'function' ? nextValue(previous[activeTab] || false) : nextValue
        }));
    }, [activeTab]);

    const setGridFilterModel = useCallback((nextValue) => {
        setGridFilterModelByTab((previous) => ({
            ...previous,
            [activeTab]: typeof nextValue === 'function' ? nextValue(previous[activeTab] || { items: [] }) : nextValue
        }));
    }, [activeTab]);

    const setGridDensity = useCallback((nextValue) => {
        setGridDensityByTab((previous) => ({
            ...previous,
            [activeTab]: nextValue
        }));
    }, [activeTab]);

    const setPaginationModel = useCallback((nextValue) => {
        setPaginationModelByTab((previous) => ({
            ...previous,
            [activeTab]: typeof nextValue === 'function' ? nextValue(previous[activeTab] || { pageSize: 1000, page: 0 }) : nextValue
        }));
    }, [activeTab]);

    const setColumnVisibilityModel = useCallback((nextValue) => {
        setColumnVisibilityModelByTab((previous) => ({
            ...previous,
            [activeTab]: typeof nextValue === 'function' ? nextValue(previous[activeTab] || {}) : nextValue
        }));
    }, [activeTab]);

    const isSystemActionUser = (actionByValue = '') => {
        const normalizedActionBy = String(actionByValue).trim().toLowerCase();
        return normalizedActionBy === 'system'
            || normalizedActionBy.includes('system')
            || normalizedActionBy.includes('system action')
            || normalizedActionBy.includes('automated')
            || normalizedActionBy.includes('automation')
            || normalizedActionBy.includes('scheduler');
    };

    const availableUserOptions = useMemo(() => {
        const usersFromGridRows = [...new Set(allCompletedRows.map((row) => row.actionBy).filter(Boolean))]
            .map((userName, index) => ({
                id: `dynamic-${index}`,
                type: userName,
                label: userName,
                value: userName
            }));

        const mergedUserOptionsByValue = new Map();
        [...userOptions, ...usersFromGridRows].forEach((option) => {
            mergedUserOptionsByValue.set(option.value, option);
        });

        const mergedUserOptions = Array.from(mergedUserOptionsByValue.values());
        if (includeSystemActions) {
            return mergedUserOptions;
        }

        return mergedUserOptions.filter((option) => !isSystemActionUser(option.value));
    }, [allCompletedRows, includeSystemActions, userOptions]);

    // System-action users still count toward search results/selection state, but should never show as a row in the dropdown itself
    const visibleUserOptions = useMemo(() => {
        return availableUserOptions.filter((option) => option.value === 'All' || !isSystemActionUser(option.value));
    }, [availableUserOptions]);

    // Only show the "All" row's checkbox as checked when every selectable option is explicitly selected
    const isAllWorkflowsSelected = useMemo(() => {
        const selectableCount = workflowOptions.filter((option) => option.value !== 'All').length;
        return selectableCount > 0 && (searchFormData.workflowSelect ?? []).length === selectableCount;
    }, [workflowOptions, searchFormData.workflowSelect]);

    const isAllQueuesSelected = useMemo(() => {
        const selectableCount = queueOptions.filter((option) => option.value !== 'All').length;
        return selectableCount > 0 && (searchFormData.queueSelect ?? []).length === selectableCount;
    }, [queueOptions, searchFormData.queueSelect]);

    const isAllSitesSelected = useMemo(() => {
        const selectableCount = siteOptions.filter((option) => option.value !== 'All').length;
        return selectableCount > 0 && (searchFormData.siteSelect ?? []).length === selectableCount;
    }, [siteOptions, searchFormData.siteSelect]);

    const isAllUsersSelected = useMemo(() => {
        const selectableCount = visibleUserOptions.filter((option) => option.value !== 'All').length;
        return selectableCount > 0 && (searchFormData.userSelect ?? []).length === selectableCount;
    }, [visibleUserOptions, searchFormData.userSelect]);

    const defaultPermissionsForFields = useMemo(() => ({
        clearFiltersBtn: { read: false, write: true, hide: false },
        exportDocumentsBtn: { read: false, write: true, hide: false },
        completedWorkflowActivityDgrd: { read: false, write: true, hide: false },
        documentViewerVwr: { read: false, write: true, hide: false },
        docVwr1Editor: { read: false, write: true, hide: false },
        docVwr1ToolbarItems: { read: false, write: true, hide: false },
        docVwr2Editor: { read: false, write: true, hide: false },
        docVwr2ToolbarItems: { read: false, write: true, hide: false },
        docVwrSplitBtn: { read: false, write: true, hide: false },
        btnCompletedWorkflow: { read: false, write: false, hide: false },
        btnCompletedQA: { read: false, write: false, hide: false },
        btnCompletedVariance: { read: false, write: false, hide: false },
        qaBtn: { read: false, write: false, hide: true },
        userDropdownSelect: { read: false, write: false, hide: true },
        completedDoubleClickEvent: { read: false, write: true, hide: false },
    }), []);
    const [formFieldAccess, setFormFieldAccess] = useState(defaultPermissionsForFields);

    const hasVisiblePermission = useCallback((controlNames = []) => {
        for (const controlName of controlNames) {
            const permission = formFieldAccess?.[controlName];
            if (permission && !(permission.hide ?? true)) {
                return true;
            }
        }

        return false;
    }, [formFieldAccess]);

    const hasWritablePermission = useCallback((controlNames = []) => {
        for (const controlName of controlNames) {
            const permission = formFieldAccess?.[controlName];
            if (permission && !(permission.hide ?? true) && (permission.write ?? false)) {
                return true;
            }
        }

        return false;
    }, [formFieldAccess]);

    const canSeeQAButton = hasVisiblePermission(['qaBtn']);
    const canWriteQAButton = hasWritablePermission(['qaBtn']);
    const canSeeUserDropdown = hasVisiblePermission(['userDropdownSelect']);
    const canWriteUserDropdown = hasWritablePermission(['userDropdownSelect']);

    useEffect(() => {
        document.title = 'YTrac :: Completed';
    }, []); // Set page title on component mount

    useEffect(() => {
        const mapWorkflowOption = (workflow, index) => {
            const workflowId = workflow?.workflowID ?? workflow?.workflowId ?? workflow?.id ?? workflow?.value ?? index + 1;
            const workflowName = workflow?.workflowName ?? workflow?.name ?? workflow?.label ?? workflow?.type ?? workflow?.value ?? '';

            if (!workflowName) {
                return null;
            }

            return {
                id: workflowId,
                type: workflowName,
                label: workflowName,
                value: workflowName
            };
        };

        const fetchWorkflowOptions = async () => {
            try {
                const workflowList = await ApiManager.GetActiveWorkflowList();
                const mappedWorkflowOptions = workflowList
                    .map(mapWorkflowOption)
                    .filter(Boolean)
                    .sort((left, right) => left.label.localeCompare(right.label));

                const workflowOptionsWithAll = [
                    { id: 'all', type: 'All', label: 'All', value: 'All' },
                    ...mappedWorkflowOptions
                ];
                setWorkflowOptions(workflowOptionsWithAll);
            }
            catch (error) {
                await LoggerUtility.ManageErrorLogging(error, errorFormName, 'fetchWorkflowOptions');
                setWorkflowOptions([]);
            }
        };

        fetchWorkflowOptions();
    }, []);

    useEffect(() => {
        const mapSiteOption = (site, index) => {
            const siteName = site?.siteName ?? site?.SiteName ?? site?.name ?? site?.label ?? site?.type ?? site?.value ?? '';
            const siteId = site?.siteID ?? site?.siteId ?? site?.SiteID ?? site?.id ?? siteName ?? index + 1;

            if (!siteName) {
                return null;
            }

            return {
                id: siteId,
                type: siteName,
                label: siteName,
                value: siteName
            };
        };

        const fetchSiteOptions = async () => {
            if (!completedDropdownSiteID) {
                setSiteOptions([]);
                return;
            }

            try {
                const params = { YTracSiteID: completedDropdownSiteID };
                const siteList = await ApiManager.GetCompletedSites(params);
                const mappedSiteOptions = siteList
                    .map(mapSiteOption)
                    .filter(Boolean)
                    .sort((left, right) => left.label.localeCompare(right.label));

                const siteOptionsWithAll = [
                    { id: 'all-site', type: 'All', label: 'All', value: 'All' },
                    ...mappedSiteOptions
                ];
                setSiteOptions(siteOptionsWithAll);
            }
            catch (error) {
                await LoggerUtility.ManageErrorLogging(error, errorFormName, 'fetchSiteOptions');
                setSiteOptions([]);
            }
        };

        fetchSiteOptions();
    }, [completedDropdownSiteID]);

    useEffect(() => {
        const fetchHolidays = async () => {
            try {
                const today = new Date();
                const siteID = loginSiteID;
                if (!siteID) return;
                const holidays = await SharedService.getHolidayList(today, siteID);
                setHolidayList(holidays || []);
            }
            catch (error) {
                await LoggerUtility.ManageErrorLogging(error, errorFormName, 'fetchHolidays');
            }
        };
        fetchHolidays();
    }, [loginSiteID]);

    useEffect(() => {
        const mapUserOption = (user, index) => {
            const userName = user?.userName ?? user?.UserName ?? user?.g4User ?? user?.g2User ?? user?.name ?? user?.label ?? user?.type ?? user?.value ?? '';
            const userId = user?.userID ?? user?.UserID ?? user?.g4UserID ?? user?.g2UserID ?? user?.id ?? userName ?? index + 1;

            if (!userName) {
                return null;
            }

            return {
                id: userId,
                type: userName,
                label: userName,
                value: userName
            };
        };

        const fetchUserOptions = async () => {
            if (!completedDropdownSiteID || !canSeeUserDropdown) {
                setUserOptions([]);
                return;
            }

            try {
                const params = { YTracSiteID: completedDropdownSiteID };
                const userList = await ApiManager.GetCompletedUsers(params);
                const mappedUserOptions = userList
                    .map(mapUserOption)
                    .filter(Boolean)
                    .sort((left, right) => left.label.localeCompare(right.label));

                const userOptionsWithAll = [
                    { id: 'all-user', type: 'All', label: 'All', value: 'All' },
                    ...mappedUserOptions
                ];
                setUserOptions(userOptionsWithAll);
            }
            catch (error) {
                await LoggerUtility.ManageErrorLogging(error, errorFormName, 'fetchUserOptions');
                setUserOptions([]);
            }
        };

        fetchUserOptions();
    }, [completedDropdownSiteID, canSeeUserDropdown]);

    useEffect(() => {
        const mapQueueOption = (queue, index) => {
            const queueId = queue?.departmentID ?? queue?.departmentId ?? queue?.queueID ?? queue?.queueId ?? queue?.id ?? queue?.value ?? index + 1;
            const queueName = queue?.departmentName ?? queue?.queueName ?? queue?.name ?? queue?.label ?? queue?.type ?? queue?.value ?? '';

            if (!queueName) {
                return null;
            }

            return {
                id: queueId,
                type: queueName,
                label: queueName,
                value: queueName
            };
        };

        const fetchQueueOptions = async () => {
            if (!loginSiteID || !masterServerDBSchema || selectedWorkflowIds.length === 0) {
                setQueueOptions([]);
                return;
            }

            try {
                const queueLists = await Promise.all(
                    selectedWorkflowIds.map((workflowId) => ApiManager.GetActiveQueueList({
                        WorkflowIDList: workflowId,
                        YTracSiteID: loginSiteID,
                        MasterServerDBSchema: masterServerDBSchema
                    }))
                );

                const uniqueQueuesByKey = new Map();
                queueLists
                    .flat()
                    .forEach((queue, index) => {
                        const mappedQueue = mapQueueOption(queue, index);

                        if (!mappedQueue) {
                            return;
                        }

                        const dedupeKey = `${mappedQueue.id}::${mappedQueue.value}`;
                        uniqueQueuesByKey.set(dedupeKey, mappedQueue);
                    });

                const mappedQueueOptions = Array.from(uniqueQueuesByKey.values())
                    .sort((left, right) => left.label.localeCompare(right.label));

                const queueOptionsWithAll = [
                    { id: 'all-queue', type: 'All', label: 'All', value: 'All' },
                    ...mappedQueueOptions
                ];
                setQueueOptions(queueOptionsWithAll);
            }
            catch (error) {
                await LoggerUtility.ManageErrorLogging(error, errorFormName, 'fetchQueueOptions');
                setQueueOptions([]);
            }
        };

        fetchQueueOptions();
    }, [loginSiteID, masterServerDBSchema, selectedWorkflowIds]);

    // Additional fetch to ensure queue options are populated when workflowOptions or form selections
    // become available after initial load (addresses timing/race conditions).
    useEffect(() => {
        const fetchQueueOptionsRobust = async () => {
            try {
                if (!loginSiteID || !masterServerDBSchema) {
                    setQueueOptions([]);
                    return;
                }

                // Determine the currently selected workflow values (fall back to all workflowOptions)
                const selectedValues = (Array.isArray(searchFormData.workflowSelect) && searchFormData.workflowSelect.length > 0)
                    ? searchFormData.workflowSelect
                    : (workflowOptions || []).filter(o => o.value !== 'All').map(o => o.value);

                const workflowIdList = (workflowOptions || [])
                    .filter((option) => selectedValues.includes(option.value))
                    .map((option) => option.id)
                    .filter((id) => id !== undefined && id !== null && id !== '');

                if (!Array.isArray(workflowIdList) || workflowIdList.length === 0) {
                    setQueueOptions([]);
                    return;
                }

                const queueLists = await Promise.all(
                    workflowIdList.map((workflowId) => ApiManager.GetActiveQueueList({
                        WorkflowIDList: workflowId,
                        YTracSiteID: loginSiteID,
                        MasterServerDBSchema: masterServerDBSchema
                    }))
                );

                const uniqueQueuesByKey = new Map();
                (queueLists.flat() || []).forEach((queue, index) => {
                    const mappedQueue = (queue && (queue.departmentName || queue.queueName || queue.name)) ? {
                        id: queue.departmentID || queue.queueID || queue.id || index,
                        type: queue.departmentName || queue.queueName || queue.name || '',
                        label: queue.departmentName || queue.queueName || queue.name || '',
                        value: queue.departmentName || queue.queueName || queue.name || ''
                    } : null;

                    if (!mappedQueue) return;
                    const dedupeKey = `${mappedQueue.id}::${mappedQueue.value}`;
                    uniqueQueuesByKey.set(dedupeKey, mappedQueue);
                });

                const mappedQueueOptions = Array.from(uniqueQueuesByKey.values())
                    .sort((left, right) => left.label.localeCompare(right.label));

                const queueOptionsWithAll = [
                    { id: 'all-queue', type: 'All', label: 'All', value: 'All' },
                    ...mappedQueueOptions
                ];
                setQueueOptions(queueOptionsWithAll);
            }
            catch (error) {
                await LoggerUtility.ManageErrorLogging(error, errorFormName, 'fetchQueueOptionsRobust');
                setQueueOptions([]);
            }
        };

        fetchQueueOptionsRobust();
    }, [loginSiteID, masterServerDBSchema, workflowOptions, searchFormData.workflowSelect]);

    useEffect(() => {
        if (!Array.isArray(searchFormData.queueSelect) || searchFormData.queueSelect.length === 0) {
            return;
        }

        const availableQueueValues = new Set(queueOptions.map((option) => option.value));
        const nextQueueSelection = searchFormData.queueSelect.filter((queueValue) => availableQueueValues.has(queueValue));

        if (nextQueueSelection.length !== searchFormData.queueSelect.length) {
            dispatchFormData({ queueSelect: nextQueueSelection });
        }
    }, [queueOptions, searchFormData.queueSelect, dispatchFormData]);

    useEffect(() => {
        if (!Array.isArray(searchFormData.siteSelect) || searchFormData.siteSelect.length === 0) {
            return;
        }

        const availableSiteValues = new Set(siteOptions.map((option) => option.value));
        const nextSiteSelection = searchFormData.siteSelect.filter((siteValue) => availableSiteValues.has(siteValue));

        if (nextSiteSelection.length !== searchFormData.siteSelect.length) {
            dispatchFormData({ siteSelect: nextSiteSelection });
        }
    }, [siteOptions, searchFormData.siteSelect, dispatchFormData]);

    useEffect(() => {
        if (!Array.isArray(searchFormData.userSelect) || searchFormData.userSelect.length === 0) {
            return;
        }

        const availableUserValues = new Set(availableUserOptions.map((option) => option.value));
        const nextUserSelection = searchFormData.userSelect.filter((userValue) => {
            // System-action usernames are only present in availableUserOptions when the current
            // result set happens to contain a system row - don't let that prune them from the
            // selection (which would silently hide system rows until the checkbox is re-toggled).
            if (isSystemActionUser(userValue)) {
                return includeSystemActions;
            }
            return availableUserValues.has(userValue);
        });

        if (nextUserSelection.length !== searchFormData.userSelect.length) {
            dispatchFormData({ userSelect: nextUserSelection });
        }
    }, [availableUserOptions, searchFormData.userSelect, dispatchFormData, includeSystemActions]);

    // Action-By values found in the result set but absent from the base user list get added to the
    // dropdown dynamically - default them to checked so they aren't silently excluded by the user
    // filter (and stay included) the next time this tab's data is refreshed/re-searched.
    useEffect(() => {
        const baseUserValues = new Set(userOptions.map((option) => option.value));
        const dynamicUserValues = [...new Set(allCompletedRows.map((row) => row.actionBy).filter(Boolean))]
            .filter((value) => !baseUserValues.has(value) && !isSystemActionUser(value));

        if (dynamicUserValues.length === 0) {
            return;
        }

        setSearchFormDataByTab((previous) => {
            const current = previous[activeTab] || initialFormData;
            const currentUserSelect = current.userSelect ?? [];
            const additions = dynamicUserValues.filter((value) => !currentUserSelect.includes(value));
            if (additions.length === 0) return previous;
            return { ...previous, [activeTab]: { ...current, userSelect: [...currentUserSelect, ...additions] } };
        });
    }, [allCompletedRows, userOptions, activeTab, initialFormData]);

    // Auto-select all workflows for all tabs on initial load
    useEffect(() => {
        if (workflowOptions.length === 0) return;
        const allValues = workflowOptions.filter(o => o.value !== 'All').map(o => o.value);
        if (allValues.length === 0) return;
        setSearchFormDataByTab((previous) => {
            const tabs = [0, 1, 2];
            const hasAnyEmpty = tabs.some((tab) => (previous[tab]?.workflowSelect ?? []).length === 0);
            if (!hasAnyEmpty) return previous;
            const next = { ...previous };
            tabs.forEach((tab) => {
                if ((previous[tab]?.workflowSelect ?? []).length === 0) {
                    next[tab] = { ...(previous[tab] || initialFormData), workflowSelect: allValues };
                }
            });
            return next;
        });
    }, [workflowOptions, initialFormData]);

    // Auto-select all queues for active tab when options load or tab changes
    useEffect(() => {
        if (queueOptions.length === 0) return;
        const allValues = queueOptions.filter(o => o.value !== 'All').map(o => o.value);
        if (allValues.length === 0) return;
        setSearchFormDataByTab((previous) => {
            const current = previous[activeTab] || initialFormData;
            if ((current.queueSelect ?? []).length !== 0) return previous;
            return { ...previous, [activeTab]: { ...current, queueSelect: allValues } };
        });
    }, [queueOptions, activeTab, initialFormData]);

    // Auto-select all sites for active tab when options load or tab changes
    useEffect(() => {
        if (siteOptions.length === 0) return;
        const allValues = siteOptions.filter(o => o.value !== 'All').map(o => o.value);
        if (allValues.length === 0) return;
        setSearchFormDataByTab((previous) => {
            const current = previous[activeTab] || initialFormData;
            if ((current.siteSelect ?? []).length !== 0) return previous;
            return { ...previous, [activeTab]: { ...current, siteSelect: allValues } };
        });
    }, [siteOptions, activeTab, initialFormData]);

    // Auto-select all users for active tab when options load or tab changes
    useEffect(() => {
        if (userOptions.length === 0) return;
        const allValues = userOptions.filter(o => o.value !== 'All').map(o => o.value);
        if (allValues.length === 0) return;
        setSearchFormDataByTab((previous) => {
            const current = previous[activeTab] || initialFormData;
            if ((current.userSelect ?? []).length !== 0) return previous;
            return { ...previous, [activeTab]: { ...current, userSelect: allValues } };
        });
    }, [userOptions, activeTab, initialFormData]);

    useEffect(() => {
        const fetchFormFieldPermissions = async () => {
            try {
                const loginUserInfo = sessionService.getUser();
                const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;

                const params = {
                    clientID: loginStateID,
                    formID: 300 // TODO: replace with Completed page FormID
                };

                const controlSecurityListApiData = await ApiManager.FetchControlsPermissions(params);
                const actualPermissionsForFields = DeterminePermissions(controlSecurityListApiData);

                setFormFieldAccess({ ...defaultPermissionsForFields, ...actualPermissionsForFields });
            } catch (error) {
                throw LoggerUtility.ProcessErrorData(error, "fetchFormFieldPermissions", {});
            }
        };

        fetchFormFieldPermissions();
    }, [defaultPermissionsForFields]);

    const handleQACheckboxChange = useCallback((rowId, checked) => {
        setQaCheckedRowIds((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(rowId);
            } else {
                next.delete(rowId);
            }
            return next;
        });
    }, []);

    const selectableQARowIds = useMemo(() => (
        resultsCompleted
            .filter((row) => row.activeQA !== true)
            .map((row) => row.id)
    ), [resultsCompleted]);

    const selectedSelectableQACount = useMemo(() => (
        selectableQARowIds.reduce((count, rowId) => count + (qaCheckedRowIds.has(rowId) ? 1 : 0), 0)
    ), [selectableQARowIds, qaCheckedRowIds]);

    const isAllQARowsSelected = selectableQARowIds.length > 0 && selectedSelectableQACount === selectableQARowIds.length;
    const isSomeQARowsSelected = selectedSelectableQACount > 0 && !isAllQARowsSelected;

    // Refs mirror the values above so column defs stay referentially stable across
    // search/refresh/filter (grid columns array must not change identity, or MUI
    // resets user column widths/order - see repo memory notes for CompletedWorkflow.js)
    const qaCheckedRowIdsRef = useRef(qaCheckedRowIds);
    qaCheckedRowIdsRef.current = qaCheckedRowIds;
    const selectableQARowIdsRef = useRef(selectableQARowIds);
    selectableQARowIdsRef.current = selectableQARowIds;
    const isAllQARowsSelectedRef = useRef(isAllQARowsSelected);
    isAllQARowsSelectedRef.current = isAllQARowsSelected;
    const isSomeQARowsSelectedRef = useRef(isSomeQARowsSelected);
    isSomeQARowsSelectedRef.current = isSomeQARowsSelected;
    const canWriteQAButtonRef = useRef(canWriteQAButton);
    canWriteQAButtonRef.current = canWriteQAButton;

    const handleQAToggleAll = useCallback((checked) => {
        setQaCheckedRowIds((prev) => {
            const next = new Set(prev);

            selectableQARowIdsRef.current.forEach((rowId) => {
                if (checked) {
                    next.add(rowId);
                } else {
                    next.delete(rowId);
                }
            });

            return next;
        });
    }, []);

    // All possible grid columns for different views
    const allGridColumns = useMemo(() => ({
        case: { field: 'case', headerName: 'Case', flex: 1, minWidth: 100, resizable: true, hideable: true },
        ytracContactID: { field: 'ytracContactID', headerName: 'Y-Trac Contact ID', flex: 2, minWidth: 160, resizable: true, hideable: true },
        workflowNumber: { field: 'workflowNumber', headerName: 'Workflow Number', flex: 2, minWidth: 150, resizable: true, hideable: true },
        inputChannel: { field: 'inputChannel', headerName: 'Input Channel', flex: 2, minWidth: 140, resizable: true, hideable: true },
        workflow: { field: 'workflow', headerName: 'Workflow', flex: 1.5, minWidth: 120, resizable: true, hideable: true },
        queue: { field: 'queue', headerName: 'Queue', flex: 1, minWidth: 100, resizable: true, hideable: true },
        actionType: { field: 'actionType', headerName: 'Action Type', flex: 1.5, minWidth: 130, resizable: true, hideable: true },
        actionTaken: { field: 'actionTaken', headerName: 'Action Taken', flex: 2, minWidth: 140, resizable: true, hideable: true },
        dateEnteredQueue: { field: 'dateEnteredQueue', headerName: 'Date Entered Queue', flex: 2.5, minWidth: 170, resizable: true, hideable: true, renderCell: (params) => (/\badd(ed)?\b/i.test(params.row.actionTaken || '') ? '' : formatDateMMDDYYYYForSite(params.value, loginUserInfo?.timeZone)) },
        daysInQueue: { field: 'daysInQueue', headerName: 'Days in Queue', flex: 1.5, minWidth: 130, resizable: true, hideable: true },
        timeAllowedInQueue: { field: 'timeAllowedInQueue', headerName: 'Time Allowed in Queue', flex: 2.5, minWidth: 180, resizable: true, hideable: true },
        workedOnTime: { field: 'workedOnTime', headerName: 'Worked on Time (Y/N)', flex: 2.5, minWidth: 170, resizable: true, hideable: true },
        actionStarted: { field: 'actionStarted', headerName: 'Action Started', flex: 2.2, minWidth: 180, resizable: true, hideable: true, renderCell: (params) => formatDateMMDDYYYYWithTime(params.value, loginUserInfo?.timeZone) },
        actionCompleted: { field: 'actionCompleted', headerName: 'Action Completed', flex: 2.5, minWidth: 190, resizable: true, hideable: true, renderCell: (params) => formatDateMMDDYYYYWithTime(params.value, loginUserInfo?.timeZone) },
        taskWorkTime: { field: 'taskWorkTime', headerName: 'Task Work Time', flex: 1.5, minWidth: 140, resizable: true, hideable: true },
        stdTime: { field: 'stdTime', headerName: 'STD Time', flex: 1, minWidth: 100, resizable: true, hideable: true },
        varianceTime: { field: 'varianceTime', headerName: 'Variance Time', flex: 1.5, minWidth: 130, resizable: true, hideable: true },
        variancePercent: { field: 'variancePercent', headerName: 'Variance %', flex: 1.2, minWidth: 110, resizable: true, hideable: true },
        bDaysToNext: { field: 'bDaysToNext', headerName: 'B-Days to Next', flex: 1.5, minWidth: 130, resizable: true, hideable: true },
        priority: { field: 'priority', headerName: 'Priority', flex: 1, minWidth: 100, resizable: true, hideable: true },
        actionBy: { field: 'actionBy', headerName: 'Action By', flex: 1.2, minWidth: 110, resizable: true, hideable: true },
        location: { field: 'location', headerName: 'Location', flex: 1.2, minWidth: 110, resizable: true, hideable: true },
        supervisor: { field: 'supervisor', headerName: 'Supervisor', flex: 1.2, minWidth: 110, resizable: true, hideable: true },
        nextActionWorkflow: { field: 'nextActionWorkflow', headerName: 'Next Action Workflow', flex: 2.5, minWidth: 180, resizable: true, hideable: true },
        nextActionQueue: { field: 'nextActionQueue', headerName: 'Next Action Queue', flex: 2.2, minWidth: 160, resizable: true, hideable: true },
        nextActionType: { field: 'nextActionType', headerName: 'Next Action Type', flex: 2, minWidth: 150, resizable: true, hideable: true },
        nextActionDue: { field: 'nextActionDue', headerName: 'Next Action Due', flex: 1.8, minWidth: 140, resizable: true, hideable: true, valueFormatter: formatDateMMDDYYYY },
        tracNote: { field: 'tracNote', headerName: 'Trac Note', flex: 1.2, minWidth: 110, resizable: true, hideable: true },
        currentAttorney: { field: 'currentAttorney', headerName: 'Current Attorney', flex: 2, minWidth: 140, resizable: true, hideable: true },
        currentLegalSpecialist: { field: 'currentLegalSpecialist', headerName: 'Current Legal Specialist', flex: 2.8, minWidth: 190, resizable: true, hideable: true },
        legalActionType: { field: 'legalActionType', headerName: 'Legal Action Type', flex: 2.2, minWidth: 160, resizable: true, hideable: true },
        legalStatusReview: { field: 'legalStatusReview', headerName: 'Legal Status Review', flex: 2.3, minWidth: 170, resizable: true, hideable: true },
        reviewResult: { field: 'reviewResult', headerName: 'Review Result', flex: 1.5, minWidth: 130, resizable: true, hideable: true },
        reviewNotes: { field: 'reviewNotes', headerName: 'Review Notes', flex: 1.5, minWidth: 130, resizable: true, hideable: true },
        legalWorkflowOpened: { field: 'legalWorkflowOpened', headerName: 'Legal Workflow Opened', flex: 2.8, minWidth: 190, resizable: true, hideable: true, valueFormatter: formatDateMMDDYYYY },
        legalRefApprovedRejected: { field: 'legalRefApprovedRejected', headerName: 'Legal Ref Approved/Rejected', flex: 3.2, minWidth: 220, resizable: true, hideable: true, valueFormatter: formatDateMMDDYYYY },
        pleadingsPrepSentToAtty: { field: 'pleadingsPrepSentToAtty', headerName: 'Pleadings Prep/Sent to Atty', flex: 3.2, minWidth: 220, resizable: true, hideable: true, valueFormatter: formatDateMMDDYYYY },
        pleadingsApprSignedByAtty: { field: 'pleadingsApprSignedByAtty', headerName: 'Pleadings Appr/Signed by Atty', flex: 3.5, minWidth: 240, resizable: true, hideable: true, valueFormatter: formatDateMMDDYYYY },
        deliveredToClerkForFiling: { field: 'deliveredToClerkForFiling', headerName: 'Delivered to Clerk for Filing', flex: 3.2, minWidth: 220, resizable: true, hideable: true, valueFormatter: formatDateMMDDYYYY },
        dateFiledWithClerk: { field: 'dateFiledWithClerk', headerName: 'Date Filed with Clerk', flex: 2.5, minWidth: 170, resizable: true, hideable: true, valueFormatter: formatDateMMDDYYYY },
        dlvdForServiceOfProcess: { field: 'dlvdForServiceOfProcess', headerName: 'Dlvd for Service of Process', flex: 3, minWidth: 200, resizable: true, hideable: true, valueFormatter: formatDateMMDDYYYY },
        serviceType: { field: 'serviceType', headerName: 'Service Type', flex: 1.5, minWidth: 130, resizable: true, hideable: true },
        dateOfServiceFailure: { field: 'dateOfServiceFailure', headerName: 'Date of Service/Failure', flex: 2.7, minWidth: 180, resizable: true, hideable: true, valueFormatter: formatDateMMDDYYYY },
        serviceResults: { field: 'serviceResults', headerName: 'Service Results', flex: 1.8, minWidth: 140, resizable: true, hideable: true },
        hearingType: { field: 'hearingType', headerName: 'Hearing Type', flex: 1.5, minWidth: 130, resizable: true, hideable: true },
        hearingDate: { field: 'hearingDate', headerName: 'Hearing Date', flex: 1.5, minWidth: 130, resizable: true, hideable: true, valueFormatter: formatDateMMDDYYYY },
        hearingTime: { field: 'hearingTime', headerName: 'Hearing Time', flex: 1.5, minWidth: 130, resizable: true, hideable: true },
        hearingResults: { field: 'hearingResults', headerName: 'Hearing Results', flex: 1.8, minWidth: 140, resizable: true, hideable: true },
        court: { field: 'court', headerName: 'Court', flex: 1, minWidth: 100, resizable: true, hideable: true },
        courtCaseNumber: { field: 'courtCaseNumber', headerName: 'Court Case Number', flex: 2.2, minWidth: 160, resizable: true, hideable: true },
        judge: { field: 'judge', headerName: 'Judge', flex: 1, minWidth: 100, resizable: true, hideable: true },
        finalOrderDlvdToJudge: { field: 'finalOrderDlvdToJudge', headerName: 'Final Order Dlvd to Judge', flex: 3, minWidth: 200, resizable: true, hideable: true, valueFormatter: formatDateMMDDYYYY },
        finalOrderSignedByJudge: { field: 'finalOrderSignedByJudge', headerName: 'Final Order Signed by Judge', flex: 3.2, minWidth: 220, resizable: true, hideable: true, valueFormatter: formatDateMMDDYYYY },
        hearingNotes: { field: 'hearingNotes', headerName: 'Hearing Notes', flex: 1.5, minWidth: 130, resizable: true, hideable: true },
        legalWorkflowClosed: { field: 'legalWorkflowClosed', headerName: 'Legal Workflow Closed', flex: 2.8, minWidth: 190, resizable: true, hideable: true, valueFormatter: formatDateMMDDYYYY },
        referToQA: {
            field: 'referToQA',
            headerName: 'Refer to QA',
            flex: 1.2,
            minWidth: 120,
            resizable: true,
            hideable: true,
            sortable: false,
            filterable: false,
            renderHeader: () => (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', overflow: 'hidden' }}>
                    <Checkbox
                        size="small"
                        checked={isAllQARowsSelectedRef.current}
                        indeterminate={isSomeQARowsSelectedRef.current}
                        disabled={!canWriteQAButtonRef.current || selectableQARowIdsRef.current.length === 0}
                        onChange={(e) => handleQAToggleAll(e.target.checked)}
                        sx={{
                            p: 0,
                            color: '#5A73EA',
                            '&.Mui-checked': { color: '#5A73EA' },
                            '&.MuiCheckbox-indeterminate': { color: '#5A73EA' },
                            '&.Mui-disabled': { color: 'rgba(0,0,0,0.26)' }
                        }}
                    />
                    <span className="MuiDataGrid-columnHeaderTitle">Refer to QA</span>
                </Box>
            ),
            renderCell: (params) => {
                const isActiveQA = params.row.activeQA === true;
                const isChecked = isActiveQA || qaCheckedRowIdsRef.current.has(params.row.id);
                const isDisabled = isActiveQA || !canWriteQAButtonRef.current;
                return (
                    <Checkbox
                        checked={isChecked}
                        disabled={isDisabled}
                        onChange={(e) => handleQACheckboxChange(params.row.id, e.target.checked)}
                        sx={{
                            color: '#5A73EA',
                            '&.Mui-checked': { color: '#5A73EA' },
                            '&.Mui-disabled': { color: 'rgba(0,0,0,0.26)' }
                        }}
                        size="small"
                    />
                );
            }
        },
        qaResult: { field: 'qaResult', headerName: 'QA Result', flex: 1.2, minWidth: 110, resizable: true, hideable: true },
        qaBy: { field: 'qaBy', headerName: 'QA\'d By', flex: 1, minWidth: 100, resizable: true, hideable: true },
        qaDate: { field: 'qaDate', headerName: 'QA Date', flex: 1, minWidth: 100, resizable: true, hideable: true, valueFormatter: formatDateMMDDYYYY },
        qaNote: { field: 'qaNote', headerName: 'QA Note', flex: 1.2, minWidth: 110, resizable: true, hideable: true }
    }), [
        handleQACheckboxChange,
        handleQAToggleAll
    ]);

    // Column configuration for each view
    const columnConfigurations = useMemo(() => ({
        workflow: [
            'case', 'ytracContactID', 'workflowNumber', 'inputChannel', 'workflow', 'queue',
            'actionType', 'actionTaken', 'dateEnteredQueue', 'daysInQueue', 'timeAllowedInQueue',
            'workedOnTime', 'actionStarted', 'actionCompleted', 'taskWorkTime', 'stdTime',
            'varianceTime', 'variancePercent', 'bDaysToNext', 'priority', 'actionBy', 'location',
            'supervisor', 'nextActionWorkflow', 'nextActionQueue', 'nextActionType', 'nextActionDue',
            'tracNote', 'currentAttorney', 'currentLegalSpecialist', 'legalActionType', 'legalStatusReview',
            'reviewResult', 'reviewNotes', 'legalWorkflowOpened', 'legalRefApprovedRejected',
            'pleadingsPrepSentToAtty', 'pleadingsApprSignedByAtty', 'deliveredToClerkForFiling',
            'dateFiledWithClerk', 'dlvdForServiceOfProcess', 'serviceType', 'dateOfServiceFailure',
            'serviceResults', 'hearingType', 'hearingDate', 'hearingTime', 'hearingResults',
            'court', 'courtCaseNumber', 'judge', 'finalOrderDlvdToJudge', 'finalOrderSignedByJudge',
            'hearingNotes', 'legalWorkflowClosed', 'referToQA', 'qaResult', 'qaBy', 'qaDate', 'qaNote'
        ],
        qa: [
            'case', 'ytracContactID', 'inputChannel', 'workflow', 'queue', 'actionType',
            'actionTaken', 'priority', 'actionBy', 'location', 'referToQA', 'qaResult',
            'qaBy', 'qaDate', 'qaNote'
        ],
        variance: [
            'case', 'ytracContactID', 'inputChannel', 'workflow', 'queue', 'actionType',
            'actionTaken', 'taskWorkTime', 'stdTime', 'varianceTime', 'variancePercent',
            'priority', 'actionBy', 'location', 'referToQA'
        ]
    }), []);

    // Function to get columns based on active tab
    const checkboxSetFilterOperator = useMemo(() => ({
        label: 'is any of',
        value: 'isAnyOfCheckbox',
        getApplyFilterFn: (filterItem) => {
            if (!filterItem?.field || !Array.isArray(filterItem.value) || filterItem.value.length === 0) {
                return null;
            }

            const selected = new Set(filterItem.value.map((value) => String(value)));

            return (cellValue) => selected.has(String(cellValue ?? ''));
        },
        InputComponent: CheckboxSetFilterInput
    }), []);

    const stringFilterOperators = useMemo(() => {
        // drop the built-in text-input "is any of" so only our checkbox version shows
        const baseOperators = getGridStringOperators().filter((operator) => operator.value !== 'isAnyOf');
        const alreadyExists = baseOperators.some((operator) => operator.value === checkboxSetFilterOperator.value);
        return alreadyExists ? baseOperators : [...baseOperators, checkboxSetFilterOperator];
    }, [checkboxSetFilterOperator]);

    const getColumnsForView = useCallback((viewIndex) => {
        let columnKeys;
        switch(viewIndex) {
            case 0: // Workflow View
                columnKeys = columnConfigurations.workflow;
                break;
            case 1: // QA View
                columnKeys = columnConfigurations.qa;
                break;
            case 2: // Variance View
                columnKeys = columnConfigurations.variance;
                break;
            default:
                columnKeys = columnConfigurations.workflow;
        }

        if (!canSeeQAButton) {
            columnKeys = columnKeys.filter((key) => key !== 'referToQA');
        }

        return columnKeys.map(key => allGridColumns[key]).filter(Boolean).map(column => {
            if (column?.filterable === false) {
                return { ...column, pinnable: true };
            }

            const columnType = column?.type ?? 'string';
            if (columnType !== 'string') {
                return { ...column, pinnable: true };
            }

            return {
                ...column,
                pinnable: true,
                filterOperators: stringFilterOperators
            };
        });
    }, [allGridColumns, columnConfigurations, canSeeQAButton, stringFilterOperators]);

    // Get current columns based on active tab
    const currentColumns = useMemo(() => getColumnsForView(activeTab), [activeTab, getColumnsForView]);

    const [alertProps, setAlertProps] = useState({
        type: '',
        message: '',
        open: false,
    });
      
    const handleAlertClose = () => {
        setAlertProps((prevState) => ({ ...prevState, open: false }));
    };

    const showAlert = (type, message) => {
        setAlertProps({ type, message, open: true });
    };

    // eslint-disable-next-line no-unused-vars
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [actionTakenDialogOpen, setActionTakenDialogOpen] = useState(false);
    const [actionTakenDialogRow, setActionTakenDialogRow] = useState(null);

    //=======================================================================
    //  - Begin
    //=======================================================================

    const getDateValidationMessage = (startDate, endDate) => {
        const today = getTodayDate();

        if (!startDate || !endDate) {
            return 'Start Date and End Date are required.';
        }

        const validStartDate = ValidationUtility.ValidateStartDate(startDate, endDate);
        const validEndDate = ValidationUtility.ValidateEndDate(startDate, endDate);
        if (!validStartDate.hasValidData || !validEndDate.hasValidData) {
            if (startDate > endDate) {
                return 'Start Date must be on or before End Date.';
            }
            return validStartDate.validationMessage || validEndDate.validationMessage || 'Please enter a valid date and search again';
        }

        if (startDate > today || endDate > today) {
            return 'Start Date and End Date cannot be later than current date.';
        }

        return '';
    };

    const getCompletedActionDateValue = (row) => {
        const rawDate = row?.actionCompleted || row?.actionCompletedTime || row?.ActionCompletedTime || row?.actionCompletedTS;
        const timestamp = Date.parse(rawDate);
        return Number.isNaN(timestamp) ? 0 : timestamp;
    };

    const normalizeCompletedRows = (apiResults) => {
        if (!Array.isArray(apiResults)) {
            return [];
        }

        const objectRows = apiResults.filter((item) => item && typeof item === 'object' && !Array.isArray(item));
        if (objectRows.length > 0) {
            return objectRows;
        }

        const nestedArray = apiResults.find(Array.isArray);
        if (!nestedArray) {
            return [];
        }

        return nestedArray.filter((item) => item && typeof item === 'object' && !Array.isArray(item));
    };

    const formatDurationFromSeconds = (secondsValue) => {
        const totalSeconds = Number(secondsValue);

        if (!Number.isFinite(totalSeconds)) {
            return '';
        }

        const absoluteSeconds = Math.abs(totalSeconds);
        const hours = Math.floor(absoluteSeconds / 3600);
        const minutes = Math.floor((absoluteSeconds % 3600) / 60);
        const seconds = absoluteSeconds % 60;
        const duration = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

        return totalSeconds < 0 ? `(${duration})` : duration;
    };

    const formatVarianceTime = (value) => {
        if (value === null || value === undefined || value === '') return '';
        const duration = String(value).trim();
        return duration.startsWith('-') ? `(${duration.slice(1)})` : duration;
    };

    const formatVariancePercent = (workSecondsValue, standardSecondsValue) => {
        const workSeconds = Number(workSecondsValue);
        const standardSeconds = Number(standardSecondsValue);

        if (!Number.isFinite(workSeconds) || !Number.isFinite(standardSeconds) || standardSeconds === 0) {
            return '';
        }

        // Round to nearest whole percent for display on Completed tab
        const percent = ((workSeconds - standardSeconds) / standardSeconds) * 100;
        return `${Math.round(percent)}%`;
    };

    // Business days elapsed strictly between action started and action completed (same-day = 0)
    const calculateBusinessDaysBetween = (startDateStr, endDateStr) => {
        if (!startDateStr || !endDateStr) return '';
        const parseDateOnly = (str) => {
            const datePart = String(str).split('T')[0];
            const parts = datePart.split('-').map(Number);
            if (parts.length < 3 || parts.some((p) => isNaN(p))) return null;
            const d = new Date(parts[0], parts[1] - 1, parts[2]);
            return isNaN(d.getTime()) ? null : d;
        };
        const startDate = parseDateOnly(startDateStr);
        const endDate = parseDateOnly(endDateStr);
        if (!startDate || !endDate) return '';
        if (endDate <= startDate) return 0;

        const holidayTimestamps = new Set(
            (holidayList || []).map((h) => {
                const hd = h instanceof Date ? new Date(h.getFullYear(), h.getMonth(), h.getDate()) : null;
                return hd ? hd.getTime() : null;
            }).filter(Boolean)
        );

        let count = 0;
        const current = new Date(startDate);
        current.setDate(current.getDate() + 1);
        while (current <= endDate) {
            const day = current.getDay();
            if (day !== 0 && day !== 6 && !holidayTimestamps.has(new Date(current.getFullYear(), current.getMonth(), current.getDate()).getTime())) count++;
            current.setDate(current.getDate() + 1);
        }

        return count;
    };

    const calculateBDaysToDate = (dueDateStr) => {
        if (!dueDateStr) return null;
        const datePart = String(dueDateStr).split('T')[0];
        const parts = datePart.split('-').map(Number);
        if (parts.length < 3 || parts.some((p) => isNaN(p))) return null;
        const dueDate = new Date(parts[0], parts[1] - 1, parts[2]);
        if (isNaN(dueDate.getTime())) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (dueDate <= today) return 0;
        const holidayTimestamps = new Set(
            (holidayList || []).map((h) => {
                const hd = h instanceof Date ? new Date(h.getFullYear(), h.getMonth(), h.getDate()) : null;
                return hd ? hd.getTime() : null;
            }).filter(Boolean)
        );
        let count = 0;
        const current = new Date(today);
        current.setDate(current.getDate() + 1);
        while (current <= dueDate) {
            const day = current.getDay();
            if (day !== 0 && day !== 6 && !holidayTimestamps.has(current.getTime())) count++;
            current.setDate(current.getDate() + 1);
        }
        return count;
    };

    const mapCompletedActionRow = (item, index) => {
        const rawBDays = item.bDaysToNext || item.BDaysToNext || item.businessDaysToNext;
        const rawNextDue = item.nextActionDue || item.NextActionDue || item.nextStepDueTS || item.actionDueDate || '';
        const bDaysToNext = (rawBDays && Number(rawBDays) !== 0)
            ? rawBDays
            : (() => { const calc = calculateBDaysToDate(rawNextDue); return calc !== null ? String(calc) : (rawBDays || ''); })();

        const rawDateEntered = item.actionDueDate || '';
        const actionTakenText = item.actionTaken || item.ActionTaken || item.lastAction || '';
        const hideDateForAdd = /\badd(ed)?\b/i.test(actionTakenText || '');

        const rawActionStarted = item.actionStarted || item.actionStartedTime || item.ActionStartedTime || item.activityStartTS || '';
        const rawActionCompleted = item.actionCompleted || item.actionCompletedTime || item.ActionCompletedTime || item.lastActionDate || '';

        const computedDaysInQueue = (rawDateEntered && !hideDateForAdd)
            ? calculateBusinessDaysBetween(rawDateEntered, rawActionCompleted)
            : '';

        return ({
        ...item,
        id: item.id || item.activity_WorkflowID || item.caseWorkflowID || item.workflowActionID || item.workflowID || item.yTracWorkflowID || `${item.caseNumber || item.case || 'completed'}-${index}`,
        case: (item.case || item.caseNumber || item.CaseNumber || '').trim(),
        ytracContactID: item.ytracContactID || item.yTracContactID || item.YTracContactID || item.contactID || '',
        workflowNumber: item.workflowNumber || item.WorkflowNumber || item.caseWorkflowID || '',
        inputChannel: item.inputChannel || item.InputChannel || '',
        workflow: item.workflow || item.Workflow || item.workflowName || '',
        queue: item.queue || item.Queue || item.departmentName || item.workflowStepName || '',
        activityTypeID: item.activityTypeID || item.ActivityTypeID || item.actionTypeID || item.ActionTypeID || 0,
        actionType: item.workflowStepName || '',
        actionTaken: actionTakenText,
        dateEnteredQueue: rawDateEntered,
        daysInQueue: computedDaysInQueue,
        timeAllowedInQueue: item.timeAllowedInQueue || item.TimeAllowedInQueue || '',
        workedOnTime: item.workedOnTime || item.WorkedOnTime || '',
        actionStarted: rawActionStarted,
        actionCompleted: rawActionCompleted,
        taskWorkTime: item.taskWorkTime || item.TaskWorkTime || item.workTime || formatDurationFromSeconds(item.actionWorkTime),
        stdTime: item.stdTime || item.STDTime || formatDurationFromSeconds(item.standardTotalTime),
        varianceTime: formatVarianceTime(item.varianceTime || item.VarianceTime || formatDurationFromSeconds((Number(item.actionWorkTime) || 0) - (Number(item.standardTotalTime) || 0))),
        variancePercent: item.variancePercent || item.VariancePercent || formatVariancePercent(item.actionWorkTime, item.standardTotalTime),
        bDaysToNext,
        priority: item.priority || item.Priority || item.stepPriority || '',
        actionBy: item.actionBy || item.ActionBy || item.actionByUserName || item.lastActionUser || '',
        location: item.location || item.Location || '',
        supervisor: item.supervisor || item.Supervisor || '',
        nextActionWorkflow: item.nextActionWorkflow || item.NextActionWorkflow || item.nextWorkflowName || '',
        nextActionQueue: item.nextActionQueue || item.NextActionQueue || item.nextDepartmentName || item.nextWorkflowStepName || '',
        nextActionType: item.nextActionType || item.NextActionType || item.nextWorkflowStepName || '',
        nextActionDue: item.nextActionDue || item.NextActionDue || item.nextStepDueTS || item.actionDueDate || '',
        tracNote: item.tracNote || item.TracNote || '',
        currentAttorney: item.currentAttorney || item.CurrentAttorney || item.attorney || '',
        currentLegalSpecialist: item.currentLegalSpecialist || item.CurrentLegalSpecialist || item.legalSpecialist || '',
        legalActionType: item.legalActionType || item.LegalActionType || '',
        legalStatusReview: item.legalStatusReview || item.LegalStatusReview || item.reviewResult || '',
        reviewResult: item.reviewResult || item.ReviewResult || '',
        reviewNotes: item.reviewNotes || item.ReviewNotes || '',
        legalWorkflowOpened: item.legalWorkflowOpened || item.LegalWorkflowOpened || item.workflowOpenedTS || '',
        legalRefApprovedRejected: item.legalRefApprovedRejected || item.LegalRefApprovedRejected || item.refAppRejTS || '',
        pleadingsPrepSentToAtty: item.pleadingsPrepSentToAtty || item.PleadingsPrepSentToAtty || item.pleadingsPrepSentTS || '',
        pleadingsApprSignedByAtty: item.pleadingsApprSignedByAtty || item.PleadingsApprSignedByAtty || item.pleadingsAppSignedTS || '',
        deliveredToClerkForFiling: item.deliveredToClerkForFiling || item.DeliveredToClerkForFiling || item.deliverToClerkTS || '',
        dateFiledWithClerk: item.dateFiledWithClerk || item.DateFiledWithClerk || item.fileWithClerkTS || '',
        dlvdForServiceOfProcess: item.dlvdForServiceOfProcess || item.DlvdForServiceOfProcess || item.processServMailTS || '',
        serviceType: item.serviceType || item.ServiceType || '',
        dateOfServiceFailure: item.dateOfServiceFailure || item.DateOfServiceFailure || item.serviceFailureTS || '',
        serviceResults: item.serviceResults || item.ServiceResults || item.serviceResult || '',
        hearingType: item.hearingType || item.HearingType || item.courtHearingType || '',
        hearingDate: item.hearingDate || item.HearingDate || item.courtHearingDateOnly || '',
        hearingTime: item.hearingTime || item.HearingTime || item.courtHearingTimeOnly || '',
        hearingResults: item.hearingResults || item.HearingResults || item.courtHearingResult || '',
        court: item.court || item.Court || '',
        courtCaseNumber: item.courtCaseNumber || item.CourtCaseNumber || item.courtNumber || '',
        judge: item.judge || item.Judge || '',
        finalOrderDlvdToJudge: item.finalOrderDlvdToJudge || item.FinalOrderDlvdToJudge || item.orderDeliveredTS || '',
        finalOrderSignedByJudge: item.finalOrderSignedByJudge || item.FinalOrderSignedByJudge || item.orderSignedTS || '',
        hearingNotes: item.hearingNotes || item.HearingNotes || '',
        legalWorkflowClosed: item.legalWorkflowClosed || item.LegalWorkflowClosed || item.workflowClosedTS || '',
        referToQA: item.referToQA || item.ReferToQA || (Number(item.activeQA) === 1 ? 'Yes' : 'No'),
        activeQA: Number(item.activeQA) === 1,
        qaResult: item.qaResult || item.QAResult || item.qaDispositionTypeName || '',
        qaBy: item.qaBy || item.QABy || item.qaUserName || '',
        qaDate: item.qaDate || item.QADate || item.qaDispositionTS || '',
        qaNote: item.qaNote || item.QANote || item.qaTracNote || ''
    });
    };

    const applyClientFilters = (rows, formData = searchFormData, includeSystemActionRows = includeSystemActions) => {
        let filteredRows = rows;

        const isAllSelected = (selectedValues = [], options = []) => {
            if (!Array.isArray(selectedValues) || selectedValues.length === 0) {
                return false;
            }

            const optionValues = options
                .map((option) => option?.value)
                .filter((value) => value && value !== 'All');

            if (optionValues.length === 0) {
                return false;
            }

            return optionValues.every((value) => selectedValues.includes(value));
        };

        if (!includeSystemActionRows) {
            filteredRows = filteredRows.filter((row) => !isSystemActionUser(row.actionBy));
        }

        if (formData.caseSearchTxt) {
            const caseSearchValue = formData.caseSearchTxt.trim().toLowerCase();
            filteredRows = filteredRows.filter((row) =>
                (row.case || '').trim().toLowerCase() === caseSearchValue
            );
        }

        const shouldFilterByWorkflow = Array.isArray(formData.workflowSelect)
            && formData.workflowSelect.length > 0
            && !isAllSelected(formData.workflowSelect, workflowOptions);

        if (shouldFilterByWorkflow) {
            filteredRows = filteredRows.filter((row) => formData.workflowSelect.includes(row.workflow));
        }

        const shouldFilterByQueue = Array.isArray(formData.queueSelect)
            && formData.queueSelect.length > 0
            && !isAllSelected(formData.queueSelect, queueOptions);

        if (shouldFilterByQueue) {
            filteredRows = filteredRows.filter((row) => formData.queueSelect.includes(row.queue));
        }

        const shouldFilterBySite = Array.isArray(formData.siteSelect)
            && formData.siteSelect.length > 0
            && !isAllSelected(formData.siteSelect, siteOptions);

        if (shouldFilterBySite) {
            filteredRows = filteredRows.filter((row) => formData.siteSelect.includes(row.location));
        }

        const shouldFilterByUser = Array.isArray(formData.userSelect)
            && formData.userSelect.length > 0
            && !isAllSelected(formData.userSelect, availableUserOptions);

        if (shouldFilterByUser) {
            // System-action rows aren't offered as selectable dropdown entries, so their visibility
            // is governed solely by includeSystemActionRows above, not by userSelect membership.
            filteredRows = filteredRows.filter((row) => (includeSystemActionRows && isSystemActionUser(row.actionBy)) || formData.userSelect.includes(row.actionBy));
        }

        if (isPersonalOnly) {
            const currentUserName = `${loginUserInfo?.firstName ?? ''} ${loginUserInfo?.lastName ?? ''}`.trim();
            filteredRows = filteredRows.filter((row) => row.actionBy === currentUserName);
        }

        // Safety-net date guard: the search API's day boundaries can drift by the
        // site's UTC offset, so re-check each row's Action Completed date against
        // the requested range using the logged-in site's own time zone (not the
        // browser's) to prevent out-of-range rows (e.g. the next calendar day)
        // from slipping into the results.
        if (formData.startDateTxt && formData.endDateTxt) {
            const siteTimeZone = loginUserInfo?.timeZone;
            filteredRows = filteredRows.filter((row) => {
                const rawDate = row.actionCompleted || row.dateEnteredQueue;
                if (!rawDate) return true;
                const dateKey = UtilityLibrary.getDateKeyForTimeZone(siteTimeZone, rawDate);
                if (!dateKey) return true;
                return dateKey >= formData.startDateTxt && dateKey <= formData.endDateTxt;
            });
        }

        return [...filteredRows].sort((a, b) => getCompletedActionDateValue(b) - getCompletedActionDateValue(a));
    };

    const buildCompletedSearchPayload = (formData = searchFormData) => {
        const activeModuleID = activeTab === 0 ? 1 : activeTab === 1 ? 2 : 3;

        // The API uses an exclusive end-date comparison (< endDate), so add 1 day
        // to the user-selected end date so that all records on that day are included.
        const endDateExclusive = (() => {
            if (!formData.endDateTxt) return '';
            const [y, m, d] = formData.endDateTxt.split('-').map(Number);
            const next = new Date(Date.UTC(y, m - 1, d + 1));
            return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}-${String(next.getUTCDate()).padStart(2, '0')}`;
        })();

        return {
            startDate: `${formData.startDateTxt}T00:00:00`,
            endDate: `${endDateExclusive}T00:00:00`,
            moduleID: activeModuleID,
            howDoingUserLoginID: -999,
            exportMode: false,
            modifiedMode: false,
            genesysServerDBSchema: null,
            pageNumber: 1,
            pageSize: 500,
            sortBy: 'lastActionDate',
            sortDirection: 'desc',
        };
    };

    const MAX_COMPLETED_SEARCH_PAGES = 50;

    const fetchAllCompletedActionPages = async (basePayload) => {
        const allItems = [];
        let pageNumber = 1;

        while (pageNumber <= MAX_COMPLETED_SEARCH_PAGES) {
            const response = await ApiManager.SearchCompletedActions({ ...basePayload, pageNumber });
            const items = Array.isArray(response) ? response : (response?.items ?? []);
            allItems.push(...items);

            const totalPages = Array.isArray(response) ? 1 : (Number(response?.totalPages) || 1);
            if (items.length === 0 || pageNumber >= totalPages) {
                break;
            }
            pageNumber += 1;
        }

        return allItems;
    };

    const handleSearchCompletedClick = async () => {
        const requestTab = activeTab;
        const requestId = (completedSearchSeqRef.current[requestTab] || 0) + 1;
        completedSearchSeqRef.current[requestTab] = requestId;

        const activeFormData = searchFormData;
        const activeIncludeSystemActions = includeSystemActions;

        const isValidCaseFormat = await checkCaseNumberRegEx(activeFormData.caseSearchTxt);
        if (!isValidCaseFormat) {
            dispatchFormData({ hasValidCaseData: false });
            showAlert('error', caseNumberRegex?.caseRegexMessage || 'Invalid case number format.');
            return;
        }

        dispatchFormData({ hasValidCaseData: true });
        const validationMessage = getDateValidationMessage(activeFormData.startDateTxt, activeFormData.endDateTxt);
        if (validationMessage) {
            dispatchFormData({ hasValidDateData: false });
            showAlert('error', validationMessage);
            return;
        }

        dispatchFormData({ hasValidDateData: true });
        setIsPageLoading(true);

        try {
            const searchPayload = buildCompletedSearchPayload(activeFormData);
            const apiItems = await fetchAllCompletedActionPages(searchPayload);

            // Ignore stale responses when refresh/search is triggered multiple times.
            if (requestId !== completedSearchSeqRef.current[requestTab]) {
                return;
            }

            const normalizedRows = normalizeCompletedRows(apiItems);
            const mappedRows = normalizedRows.map(mapCompletedActionRow);
            const filteredRows = applyClientFilters(mappedRows, activeFormData, activeIncludeSystemActions);

            setPaginationModel((currentModel) => ({ ...currentModel, page: 0 }));
            setAllCompletedRows(mappedRows);
            setResultsCompleted(filteredRows);
            setQaCheckedRowIds(new Set());
        }
        catch (error) {
            if (requestId !== completedSearchSeqRef.current[requestTab]) {
                return;
            }

            await LoggerUtility.ManageErrorLogging(error, errorFormName, 'handleSearchCompletedClick');
            showAlert('error', 'Unable to search completed actions right now. Please try again.');
            setAllCompletedRows([]);
            setResultsCompleted([]);
        }
        finally {
            if (requestId === completedSearchSeqRef.current[requestTab]) {
                setIsPageLoading(false);
                setHasSearchedByTab((prev) => ({ ...prev, [requestTab]: true }));
            }
        }
    };

    const setDateValidationMessage = (startDate, endDate) => {
        const validationMessage = getDateValidationMessage(startDate, endDate);
        dispatchFormData({ hasValidDateData: validationMessage === '' });
    }

    const handleStartDateChange = (event) => {
        const { value } = event.target;
        dispatchFormData({ startDateTxt: value });
        setDateValidationMessage(value, searchFormData.endDateTxt);
    };

    const handleEndDateChange = (event) => {
        const { value } = event.target;
        dispatchFormData({ endDateTxt: value });
        setDateValidationMessage(searchFormData.startDateTxt, value);
    };

    const handleWorkflowSelectChange = (event, newValue, reason, details) => {
        if (reason === 'clear') {
            // Clicking the clear (X) button while the dropdown is open should not close it
            workflowJustClearedRef.current[activeTab] = true;
            dispatchFormData({ workflowSelect: [], queueSelect: [] });
            return;
        }
        if (details?.option?.value === 'All') {
            // Toggle: if every real option is already selected, clicking "All" again clears the selection
            const nextSelectedValues = isAllWorkflowsSelected ? [] : workflowOptions.filter(o => o.value !== 'All').map(o => o.value);
            dispatchFormData({ workflowSelect: nextSelectedValues, queueSelect: [] });
            return;
        }
        let selectedValues = Array.isArray(newValue) ? newValue.map((option) => option.value) : [];
        selectedValues = selectedValues.filter(v => v !== 'All');
        dispatchFormData({ workflowSelect: selectedValues, queueSelect: [] });
    };

    const handleQueueSelectChange = (event, newValue, reason, details) => {
        if (reason === 'clear') {
            // Clicking the clear (X) button while the dropdown is open should not close it
            queueJustClearedRef.current[activeTab] = true;
            dispatchFormData({ queueSelect: [] });
            return;
        }
        if (details?.option?.value === 'All') {
            const nextSelectedValues = isAllQueuesSelected ? [] : queueOptions.filter(o => o.value !== 'All').map(o => o.value);
            dispatchFormData({ queueSelect: nextSelectedValues });
            return;
        }
        let selectedValues = Array.isArray(newValue) ? newValue.map((option) => option.value) : [];
        selectedValues = selectedValues.filter(v => v !== 'All');
        dispatchFormData({ queueSelect: selectedValues });
    };

    const handleUserSelectChange = (event, newValue, reason, details) => {
        if (details?.option?.value === 'All') {
            const nextSelectedValues = isAllUsersSelected ? [] : visibleUserOptions.filter(o => o.value !== 'All').map(o => o.value);
            dispatchFormData({ userSelect: nextSelectedValues });
            return;
        }
        let selectedValues = Array.isArray(newValue) ? newValue.map((option) => option.value) : [];
        selectedValues = selectedValues.filter(v => v !== 'All');
        dispatchFormData({ userSelect: selectedValues });
    };

    const handleSiteSelectChange = (event, newValue, reason, details) => {
        if (details?.option?.value === 'All') {
            const nextSelectedValues = isAllSitesSelected ? [] : siteOptions.filter(o => o.value !== 'All').map(o => o.value);
            dispatchFormData({ siteSelect: nextSelectedValues });
            return;
        }
        let selectedValues = Array.isArray(newValue) ? newValue.map((option) => option.value) : [];
        selectedValues = selectedValues.filter(v => v !== 'All');
        dispatchFormData({ siteSelect: selectedValues });
    };

    // Force-close on any real outside click - MUI's built-in onClose can silently stop firing after
    // the clear (X) button is clicked while these dropdowns are open, leaving them stuck open otherwise
    useEffect(() => {
        const handleOutsideClick = (event) => {
            [0, 1, 2].forEach((tabIndex) => {
                if (workflowDropdownOpenByTab[tabIndex]) {
                    const container = workflowDropdownContainerRefs.current[tabIndex];
                    if (container && !container.contains(event.target)) {
                        setWorkflowDropdownOpenByTab((prev) => ({ ...prev, [tabIndex]: false }));
                    }
                }
                if (queueDropdownOpenByTab[tabIndex]) {
                    const container = queueDropdownContainerRefs.current[tabIndex];
                    if (container && !container.contains(event.target)) {
                        setQueueDropdownOpenByTab((prev) => ({ ...prev, [tabIndex]: false }));
                    }
                }
            });
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [workflowDropdownOpenByTab, queueDropdownOpenByTab]);

    const handleWorkflowDropdownOpen = (tabIndex) => {
        setWorkflowDropdownOpenByTab((prev) => ({ ...prev, [tabIndex]: true }));
    };

    const handleWorkflowDropdownClose = (tabIndex) => {
        if (workflowJustClearedRef.current[tabIndex]) {
            workflowJustClearedRef.current[tabIndex] = false;
            return;
        }
        setWorkflowDropdownOpenByTab((prev) => ({ ...prev, [tabIndex]: false }));
    };

    const handleQueueDropdownOpen = (tabIndex) => {
        setQueueDropdownOpenByTab((prev) => ({ ...prev, [tabIndex]: true }));
    };

    const handleQueueDropdownClose = (tabIndex) => {
        if (queueJustClearedRef.current[tabIndex]) {
            queueJustClearedRef.current[tabIndex] = false;
            return;
        }
        setQueueDropdownOpenByTab((prev) => ({ ...prev, [tabIndex]: false }));
    };

    // Shared option renderers so the "All" row's checkbox reflects isAllXSelected instead of MUI's raw per-item match
    const renderWorkflowOptionRow = (props, option, { selected }) => {
        const isChecked = option.value === 'All' ? isAllWorkflowsSelected : selected;
        return (
            <li {...props}><Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small" />} checkedIcon={<CheckBoxIcon fontSize="small" />} style={{ marginRight: 8 }} checked={isChecked} />{option.label}</li>
        );
    };

    const renderQueueOptionRow = (props, option, { selected }) => {
        const isChecked = option.value === 'All' ? isAllQueuesSelected : selected;
        return (
            <li {...props}><Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small" />} checkedIcon={<CheckBoxIcon fontSize="small" />} style={{ marginRight: 8 }} checked={isChecked} />{option.label}</li>
        );
    };

    const renderSiteOptionRow = (props, option, { selected }) => {
        const isChecked = option.value === 'All' ? isAllSitesSelected : selected;
        return (
            <li {...props}><Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small" />} checkedIcon={<CheckBoxIcon fontSize="small" />} style={{ marginRight: 8 }} checked={isChecked} />{option.label}</li>
        );
    };

    const renderUserOptionRow = (props, option, { selected }) => {
        const isChecked = option.value === 'All' ? isAllUsersSelected : selected;
        return (
            <li {...props}><Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small" />} checkedIcon={<CheckBoxIcon fontSize="small" />} style={{ marginRight: 8 }} checked={isChecked} />{option.label}</li>
        );
    };

    const checkCaseNumberRegEx = useCallback(async (caseNumber) => {
        try {
            let isValidFormat = true;
            const caseNumberRegexValue = new RegExp(caseNumberRegex.caseRegexValue || '');

            if (caseNumberRegex.caseRegexValue && caseNumber) {
                if (!caseNumberRegexValue.test(caseNumber)) {
                    if (activeCaseSearchRef && activeCaseSearchRef.setValidationMessage) {
                        activeCaseSearchRef.setValidationMessage(caseNumberRegex.caseRegexMessage || 'Invalid case number format');
                    }
                    setCaseNumberIconsDisabled({ search: true, copy: true, clear: false });
                    isValidFormat = false;
                }
            }

            if (isValidFormat) {
                if (activeCaseSearchRef && activeCaseSearchRef.setValidationMessage) {
                    activeCaseSearchRef.setValidationMessage('');
                }

                const hasCaseValue = Boolean(caseNumber);
                setCaseNumberIconsDisabled({ search: !hasCaseValue, copy: !hasCaseValue, clear: !hasCaseValue });
            }

            return isValidFormat;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, 'checkCaseNumberRegEx', {});
        }
    }, [activeCaseSearchRef, caseNumberRegex, setCaseNumberIconsDisabled]);

    const handleCaseSearchChange = async (event) => {
        try {
            const valueText = event.target.value;
            const isValidFormat = await checkCaseNumberRegEx(valueText);

            dispatchFormData({ caseSearchTxt: valueText, hasValidCaseData: isValidFormat });
        }
        catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, 'handleCaseSearchChange');
            dispatchFormData({ caseSearchTxt: event.target.value, hasValidCaseData: true });
        }
    };

    const handleCaseSearchClick = async () => {
        await handleSearchCompletedClick();
    };

    const handleRefreshCompletedClick = async () => {
        await handleSearchCompletedClick();
    };

    const handleFilterKeyDown = async (event) => {
        if (event.key !== 'Enter') {
            return;
        }

        const isGridElement = event.target.closest('.MuiDataGrid-root');
        if (isGridElement) {
            return;
        }

        event.preventDefault();
        await handleSearchCompletedClick();
    };

    const handleCaseSearchClear = () => {
        dispatchFormData({ caseSearchTxt: "", hasValidCaseData: true });
        setCaseNumberIconsDisabled({ search: true, copy: true, clear: true });
        if (activeCaseSearchRef && activeCaseSearchRef.setValidationMessage) {
            activeCaseSearchRef.setValidationMessage("");
        }
    };

    const handleRowDoubleClick = (params) => {
        if (!(formFieldAccess.completedDoubleClickEvent?.hide ?? false)) {
            setSelectedRowData(params.row);
            setActionTakenDialogRow(params.row);
            setActionTakenDialogOpen(true);
        } else {
            showAlert('error', 'Access denied.');
        }
    };

    const handleCloseActionTakenDialog = () => {
        setActionTakenDialogOpen(false);
        setActionTakenDialogRow(null);
    };

    const handleStartQAWorkflowClick = () => {
        if (qaCheckedRowIds.size === 0) return;
        setQaConfirmDialogOpen(true);
    };

    const handleQAConfirmCancel = () => {
        setQaConfirmDialogOpen(false);
        setQaCheckedRowIds(new Set());
    };

    const handleQAConfirmOK = async () => {
        setQaConfirmDialogOpen(false);
        const selectedIds = Array.from(qaCheckedRowIds);
        const count = selectedIds.length;

        try {
            const selectedRows = resultsCompleted.filter((row) => selectedIds.includes(row.id));
            const userLoginID = loginUserInfo?.legacyUserID || loginUserInfo?.userLoginID || null;
            const yTracSiteID = loginUserInfo?.siteID || loginSiteID || null;

            const payloads = selectedRows.map((row) => ({
                activityWorkflowID: row.id,
                caseNumber: row.case,
                userLoginID,
                yTracSiteID,
                noteID: null,
                activityTypeID: row.activityTypeID || 0,
                workflowStartID: 2,
                masterServerDBSchema
            }));

            console.log('[handleQAConfirmOK] Submitting', payloads.length, 'QA workflow(s)');
            console.log('[handleQAConfirmOK] Payloads:', JSON.stringify(payloads));

            await ApiManager.StartQAWorkflows(payloads);
        } catch (error) {
            console.error('[handleQAConfirmOK] Error:', error);
            await LoggerUtility.ManageErrorLogging(error, errorFormName, 'handleQAConfirmOK');
        }

        // Mark those rows as having an active QA workflow (checked, disabled)
        setResultsCompleted((prev) =>
            prev.map((row) =>
                selectedIds.includes(row.id)
                    ? { ...row, activeQA: true }
                    : row
            )
        );
        setAllCompletedRows((prev) =>
            prev.map((row) =>
                selectedIds.includes(row.id)
                    ? { ...row, activeQA: true }
                    : row
            )
        );
        setQaCheckedRowIds(new Set());
        setQaSuccessCount(count);
        setQaSuccessDialogOpen(true);
    };

    const handleQASuccessClose = () => {
        setQaSuccessDialogOpen(false);
    };

    const handleExportBtnClick = () => {
        const defaultName = getExportFileName();
        setFileName(defaultName);
        setExportDialogOpen(true);
    };

    const handleExportDialogClose = () => {
        setExportDialogOpen(false);
    };

    const handleFileNameChange = (event) => {
        setFileName(event.target.value);
    };

    const handleFormatChange = (event) => {
        setFormat(event.target.value);
    };

    const handlePageSelectionChange = (event) => {
        setPageSelection(event.target.value);
    };

    const handleIncludeFiltersChange = (event) => {
        setIncludeFilters(event.target.checked);
    };

    const getScopeRowIds = () => {
        const activeGridApiRef = getGridApiRefForTab(activeTab);
        const allVisibleRowIds = typeof activeGridApiRef.current?.getSortedRowIds === 'function'
            ? activeGridApiRef.current.getSortedRowIds()
            : resultsCompleted.map((row) => row.id);

        if (exportScope === 'currentPage') {
            const pageSize = paginationModelByTab[activeTab]?.pageSize || 1000;
            const pageNum = paginationModelByTab[activeTab]?.page || 0;
            const startIndex = pageNum * pageSize;
            const endIndex = startIndex + pageSize;
            return allVisibleRowIds.slice(startIndex, endIndex);
        }

        return allVisibleRowIds;
    };

    const getExportFileName = () => {
        const tabNames = ['Workflow', 'QA', 'Variance'];
        const tabName = tabNames[activeTab] || 'Completed';
        const user = sessionService.getUser() || {};
        const stateName = String(user.stateName || 'State').trim().replace(/\s+/g, '_');
        const siteName = String(user.siteName || 'Site').trim().replace(/\s+/g, '_');
        const now = new Date();
        const yyyymmdd = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;

        return `${stateName}_${siteName}_${tabName}_Data_${yyyymmdd}`;
    };

    const handleExportExecute = async () => {
        try {
            const activeGridApiRef = getGridApiRefForTab(activeTab);
            const visibleFields = gridVisibleColumnFieldsSelector(activeGridApiRef);

            const fieldsToExport = currentColumns.map((column) => column.field);

            const defaultName = getExportFileName();
            const typedName = (fileName || '').trim();
            const nameToUse = typedName.length > 0 ? typedName : defaultName;
            const isMismatch = typedName.length > 0 && typedName !== defaultName;
            const finalName = isMismatch ? typedName : nameToUse;

            const api = activeGridApiRef?.current;

            if (!api) {
                showAlert('error', 'Grid is not ready for export.');
                return;
            }

            const unfilteredRowIds =
                (typeof api.getSortedRowIds === 'function' && api.getSortedRowIds()) ||
                (typeof api.getRowModels === 'function' && Array.from(api.getRowModels().keys())) ||
                resultsCompleted.map((row) => row.id);

            let filteredRowIds = unfilteredRowIds;
            if (api.state) {
                try {
                    filteredRowIds = gridFilteredSortedRowIdsSelector(activeGridApiRef) || unfilteredRowIds;
                } catch {
                    filteredRowIds = unfilteredRowIds;
                }
            }

            let rowIdsToExport;
            let exportFields;
            let allColumnsFlag;

            if (includeFilters === true) {
                if (pageSelection === 'all') {
                    rowIdsToExport = filteredRowIds;
                    exportFields = visibleFields;
                    allColumnsFlag = false;
                } else if (pageSelection === 'current') {
                    const source = filteredRowIds;
                    const pageSize = paginationModelByTab[activeTab]?.pageSize || 1000;
                    const pageNum = paginationModelByTab[activeTab]?.page || 0;
                    const start = pageNum * pageSize;
                    rowIdsToExport = source.slice(start, start + pageSize);
                    exportFields = visibleFields;
                    allColumnsFlag = false;
                }
            } else {
                if (pageSelection === 'all') {
                    rowIdsToExport = unfilteredRowIds;
                    exportFields = currentColumns.map(col => col.field);
                    allColumnsFlag = true;
                } else if (pageSelection === 'current') {
                    const source = unfilteredRowIds;
                    const pageSize = paginationModelByTab[activeTab]?.pageSize || 1000;
                    const pageNum = paginationModelByTab[activeTab]?.page || 0;
                    const start = pageNum * pageSize;
                    rowIdsToExport = source.slice(start, start + pageSize);
                    exportFields = currentColumns.map(col => col.field);
                    allColumnsFlag = false;
                }
            }

            const exportOptions = {
                csv: () => activeGridApiRef.current.exportDataAsCsv({
                    fileName: finalName,
                    includeHeaders: includeHeaders,
                    fields: exportFields,
                    allColumns: allColumnsFlag,
                    includeFilters: includeFilters,
                    getRowsToExport: () => rowIdsToExport
                }),
                excel: () => activeGridApiRef.current.exportDataAsExcel({
                    fileName: finalName,
                    includeHeaders: includeHeaders,
                    fields: exportFields,
                    allColumns: allColumnsFlag,
                    includeFilters: includeFilters,
                    getRowsToExport: () => rowIdsToExport
                }),
            };

            if (format === 'csv') {
                exportOptions.csv();
                showAlert('success', 'CSV export initiated!');
            } else if (format === 'excel') {
                exportOptions.excel();
                showAlert('success', 'Excel export initiated!');
            } else {
                showAlert('error', 'Please select a valid export format.');
                return;
            }
        } catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, 'handleExportExecute');
            showAlert('error', `Unable to export data to the selected format ${format}. Please try again or choose a different format.`);
        } finally {
            setExportDialogOpen(false);
        }
    };

    const handleCopyAllWithHeaders = async () => {
        const activeGridApiRef = getGridApiRefForTab(activeTab);
        const api = activeGridApiRef.current;
        if (!api || typeof api.getSortedRowIds !== 'function') {
            showAlert('error', 'Grid is not ready for copy.');
            return;
        }

        const fieldsInOrder = api.getVisibleColumns().map((column) => column.field);
        const unfilteredRowIds = api.getSortedRowIds();
        let rowIdsInOrder = unfilteredRowIds;
        try {
            rowIdsInOrder = gridFilteredSortedRowIdsSelector(activeGridApiRef) || unfilteredRowIds;
        } catch {
            rowIdsInOrder = unfilteredRowIds;
        }

        const headerRow = fieldsInOrder.map((field) => api.getColumn(field)?.headerName || field).join('\t');
        const dataRows = rowIdsInOrder.map((rowId) => (
            fieldsInOrder.map((field) => {
                const cellValue = api.getCellParams(rowId, field)?.formattedValue ?? api.getCellValue(rowId, field);
                return cellValue == null ? '' : String(cellValue);
            }).join('\t')
        ));

        const clipboardText = [headerRow, ...dataRows].join('\n');

        try {
            await navigator.clipboard.writeText(clipboardText);
            showAlert('success', 'Results copied to clipboard!');
        } catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, 'handleCopyAllWithHeaders');
            showAlert('error', 'Unable to copy results. Please try again.');
        }
    };

    const handleGridKeyDown = (event) => {
        // Enhanced keyboard navigation
        if (event.key === 'Enter' || event.key === ' ') {
            // Handle row selection/activation
            event.preventDefault();
            if (event.target.closest('.MuiDataGrid-row')) {
                const rowId = event.target.closest('.MuiDataGrid-row').getAttribute('data-id');
                console.log('Row activated via keyboard:', rowId);
            }
        }

        const isCopyShortcut = (event.ctrlKey || event.metaKey) && (event.key === 'c' || event.key === 'C');
        if (isCopyShortcut) {
            const api = getGridApiRefForTab(activeTab).current;
            if (!api || typeof api.getSelectedCellsAsArray !== 'function') {
                return;
            }
            const selectedCells = api.getSelectedCellsAsArray();
            if (!selectedCells || selectedCells.length === 0) {
                return;
            }

            const selectedFieldSet = new Set(selectedCells.map((cell) => cell.field));
            const selectedRowIdSet = new Set(selectedCells.map((cell) => cell.id));

            const fieldsInOrder = api.getVisibleColumns()
                .map((column) => column.field)
                .filter((field) => selectedFieldSet.has(field));
            const rowIdsInOrder = api.getSortedRowIds().filter((rowId) => selectedRowIdSet.has(rowId));

            const headerRow = fieldsInOrder.map((field) => api.getColumn(field)?.headerName || field).join('\t');
            const dataRows = rowIdsInOrder.map((rowId) => (
                fieldsInOrder.map((field) => {
                    const cellValue = api.getCellParams(rowId, field)?.formattedValue ?? api.getCellValue(rowId, field);
                    return cellValue == null ? '' : String(cellValue);
                }).join('\t')
            ));

            const clipboardText = [headerRow, ...dataRows].join('\n');
            if (navigator.clipboard && navigator.clipboard.writeText) {
                event.preventDefault();
                navigator.clipboard.writeText(clipboardText);
            }
        }
    };

    const normalizeMultiFilterModel = useCallback((incomingModel) => {
        const safeModel = incomingModel && typeof incomingModel === 'object'
            ? incomingModel
            : { items: [] };

        const normalizedItems = Array.isArray(safeModel.items)
            ? safeModel.items.map((item, index) => ({
                ...item,
                id: item?.id ?? `${item?.field || 'field'}-${item?.operator || 'op'}-${index}`
            }))
            : [];

        return {
            ...safeModel,
            items: normalizedItems
        };
    }, []);

    const handleGridFilterChange = (filterModel) => {
        setGridFilterModel(normalizeMultiFilterModel(filterModel));
    };

    const captureGridColumnState = useCallback((tabIndex) => {
        const gridApiRef = getGridApiRefForTab(tabIndex);
        if (gridApiRef.current?.exportState) {
            const state = gridApiRef.current.exportState();
            // pinnedColumns lives outside the `columns` section of exportState - must be captured/restored separately
            savedGridStateByTab.current[tabIndex] = { columns: state.columns, pinnedColumns: state.pinnedColumns };
        }
    }, [getGridApiRefForTab]);

    const handleGridColumnOrderChange = useCallback(() => {
        captureGridColumnState(activeTab);
    }, [activeTab, captureGridColumnState]);

    const handleColumnWidthChange = useCallback(() => {
        captureGridColumnState(activeTab);
    }, [activeTab, captureGridColumnState]);

    const handlePinnedColumnsChange = useCallback(() => {
        captureGridColumnState(activeTab);
    }, [activeTab, captureGridColumnState]);

    const handleColumnVisibilityModelChange = (model) => {
        setColumnVisibilityModel(model);
    };

    const handleOpenColumnsPanel = () => {
        if (!resultsCompleted.length) {
            return;
        }

        getGridApiRefForTab(activeTab).current.showPreferences(
            GridPreferencePanelsValue.columns,
            'completed-columns-panel',
            'completed-columns-button'
        );
    };

    const handleOpenFiltersPanel = () => {
        if (!resultsCompleted.length) {
            return;
        }

        getGridApiRefForTab(activeTab).current.showPreferences(
            GridPreferencePanelsValue.filters,
            'completed-filters-panel',
            'completed-filters-button'
        );
    };

    const handleOpenDensityMenu = (event) => {
        if (!resultsCompleted.length) {
            return;
        }

        setDensityMenuAnchorEl(event.currentTarget);
    };

    const handleCloseDensityMenu = () => {
        setDensityMenuAnchorEl(null);
    };

    const handleDensityChange = (newDensity) => {
        setGridDensity(newDensity);
        const activeGridApiRef = getGridApiRefForTab(activeTab);
        if (activeGridApiRef.current?.setDensity) {
            activeGridApiRef.current.setDensity(newDensity);
        }
        handleCloseDensityMenu();
    };

    const handleGridClearFilters = () => {
        const today = getTodayDate();
    
        const allWorkflowValues = workflowOptions.filter(o => o.value !== 'All').map(o => o.value);
        const allSiteValues = siteOptions.filter(o => o.value !== 'All').map(o => o.value);
        // Base user list plus any Action-By users found in results but not yet in userOptions
        const baseUserValues = userOptions.filter(o => o.value !== 'All').map(o => o.value);
        const dynamicUserValues = [...new Set(allCompletedRows.map((row) => row.actionBy).filter(Boolean))]
            .filter((value) => !baseUserValues.includes(value) && !isSystemActionUser(value));
        const allUserValues = [...baseUserValues, ...dynamicUserValues];
        dispatchFormData({
            ...initialFormData,
            startDateTxt: today,
            endDateTxt: today,
            hasValidDateData: true,
            workflowSelect: allWorkflowValues,
            // queueOptions is scoped to the currently-selected workflows, so it's stale here for any
            // workflows that were deselected - leave queueSelect empty and let the auto-select-all-queues
            // effect populate it once queueOptions refetches against the now-fully-selected workflows.
            queueSelect: [],
            siteSelect: allSiteValues,
            userSelect: allUserValues
        });
        setIncludeSystemActions(false);
        setCaseNumberIconsDisabled(defaultCaseNumberIconsDisabled);
        if (activeCaseSearchRef && activeCaseSearchRef.setValidationMessage) {
            activeCaseSearchRef.setValidationMessage("");
        }
        setPaginationModel((previous) => ({ ...previous, page: 0 }));
    };

    const handleTabChange = (event, newValue) => {
        // Capture the outgoing tab's grid column state before switching so it isn't lost
        captureGridColumnState(activeTab);
        setActiveTab(newValue);
        setDensityMenuAnchorEl(null);
    };

    const handleIncludeSystemActionsChange = (event) => {
        // System-action rows are never offered as selectable userSelect entries - the checkbox alone
        // gates their visibility in applyClientFilters, so userSelect doesn't need to track them.
        setIncludeSystemActions(event.target.checked);
    };

    const handleCloseNotificationDialog = () => {
        setNotificationDialogOpen(false);
        setNotificationDialogContent([]);
    };

    // SAVE SESSION STATE TO SESSION STORAGE - runs on every relevant change (not just unmount),
    // since a hard refresh/full navigation never invokes an effect's cleanup function.
    useEffect(() => {
        // Skip until the mount-time restore effect has run, or this overwrites saved data with fresh/default state first
        if (!restoreCompleted) {
            return;
        }

        const persistState = () => {
            captureGridColumnState(activeTab);
            try {
                sessionStorage.setItem('completedPageState', JSON.stringify({
                    searchFormDataByTab,
                    resultsCompletedByTab,
                    allCompletedRowsByTab,
                    paginationModelByTab,
                    columnVisibilityModelByTab,
                    gridFilterModelByTab,
                    gridDensityByTab,
                    includeSystemActionsByTab,
                    hasSearchedByTab,
                    activeTab,
                    savedGridStateByTab: savedGridStateByTab.current
                }));
            } catch (e) {
                // sessionStorage quota exceeded (large grid data); drop row snapshots, keep filters/column state
                try {
                    sessionStorage.setItem('completedPageState', JSON.stringify({
                        searchFormDataByTab,
                        paginationModelByTab,
                        columnVisibilityModelByTab,
                        gridFilterModelByTab,
                        gridDensityByTab,
                        includeSystemActionsByTab,
                        hasSearchedByTab,
                        activeTab,
                        savedGridStateByTab: savedGridStateByTab.current
                    }));
                } catch (e2) {
                    // ignore if still fails
                }
            }
        };

        persistState();
        return persistState;
    }, [restoreCompleted, searchFormDataByTab, resultsCompletedByTab, allCompletedRowsByTab, paginationModelByTab, columnVisibilityModelByTab, gridFilterModelByTab, gridDensityByTab, includeSystemActionsByTab, hasSearchedByTab, activeTab]);

    // RESTORE SESSION STATE FROM SESSION STORAGE - mount only
    useEffect(() => {
        const saved = sessionStorage.getItem('completedPageState');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (parsed.searchFormDataByTab) setSearchFormDataByTab(parsed.searchFormDataByTab);
                if (parsed.resultsCompletedByTab) setResultsCompletedByTab(parsed.resultsCompletedByTab);
                if (parsed.allCompletedRowsByTab) setAllCompletedRowsByTab(parsed.allCompletedRowsByTab);
                if (parsed.paginationModelByTab) setPaginationModelByTab(parsed.paginationModelByTab);
                if (parsed.columnVisibilityModelByTab) setColumnVisibilityModelByTab(parsed.columnVisibilityModelByTab);
                if (parsed.gridFilterModelByTab) setGridFilterModelByTab(parsed.gridFilterModelByTab);
                if (parsed.gridDensityByTab) setGridDensityByTab(parsed.gridDensityByTab);
                if (parsed.includeSystemActionsByTab) setIncludeSystemActionsByTab(parsed.includeSystemActionsByTab);
                if (parsed.hasSearchedByTab) setHasSearchedByTab(parsed.hasSearchedByTab);
                if (parsed.activeTab !== undefined) setActiveTab(parsed.activeTab);
                if (parsed.savedGridStateByTab) savedGridStateByTab.current = parsed.savedGridStateByTab;
            } catch (e) {
                sessionStorage.removeItem('completedPageState');
            }
        }
        setRestoreCompleted(true);
    }, []);

    // Re-apply the active tab's saved column order/width once state has been restored
    useEffect(() => {
        if (!restoreCompleted || !hasSearchedByTab[activeTab]) {
            return;
        }
        const saved = savedGridStateByTab.current[activeTab];
        const activeGridApiRef = getGridApiRefForTab(activeTab);
        if (!saved) {
            return;
        }
        // On a fresh page mount the grid hasn't finished its first layout/measurement pass yet,
        // so calling restoreState() immediately can get silently overwritten - defer past that pass.
        let rafId2;
        const rafId1 = requestAnimationFrame(() => {
            rafId2 = requestAnimationFrame(() => {
                if (activeGridApiRef.current?.restoreState) {
                    activeGridApiRef.current.restoreState(saved);
                }
            });
        });
        return () => {
            cancelAnimationFrame(rafId1);
            if (rafId2) cancelAnimationFrame(rafId2);
        };
    }, [activeTab, restoreCompleted, hasSearchedByTab, getGridApiRefForTab]);

    const renderWorkflowForm = (activeForm) => {
        return (
            <Box
                sx={{
                    display: activeForm ? 'block' : 'none'
                }}
            >
                <div style={{ width: "100%" }}>
                    {/* Filter Row - responsive layout for different screen sizes */}
                    <div style={{ 
                        display: "flex", 
                        flexWrap: "wrap",
                        alignItems: "center", 
                        gap: "12px", 
                        padding: "16px 24px", 
                        backgroundColor: "#ffffffff", 
                        minHeight: "70px"
                    }} onKeyDown={handleFilterKeyDown}>
                        {/* Date Section - responsive container */}
                        <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: "8px",
                            flexWrap: "nowrap",
                            flexShrink: 1,
                            minWidth: "0"
                        }}>
                            {/* Start Date Field */}
                            <div style={{ flex: "0 1 220px", minWidth: "150px", maxWidth: "306px" }}>
                                <FormControl fullWidth>
                                <TextField 
                                    label="Start Date" 
                                    type="date" 
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: getTodayDate() }}
                                    size='small'
                                    id="startDateTxt"
                                    name="startDateTxt"
                                    value={searchFormData.startDateTxt}
                                    onChange={handleStartDateChange}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => dispatchFormData({ startDateTxt: "" })}
                                                    size="small"
                                                    sx={{ padding: '4px' }}
                                                >
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover fieldset': {
                                                borderColor: '#5A73EA',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#5A73EA',
                                            },
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#5A73EA',
                                        },
                                        '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                            filter: 'invert(40%) sepia(24%) saturate(3168%) hue-rotate(207deg) brightness(102%) contrast(84%)',
                                        },
                                    }}
                                />
                            </FormControl>
                        </div>
                        
                        
                        {/* End Date Field */}
                        <div style={{ flex: "0 1 220px", minWidth: "150px", maxWidth: "306px" }}>
                            <FormControl fullWidth>
                                <TextField 
                                    label="End Date" 
                                    type="date" 
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: getTodayDate() }}
                                    size='small'
                                    id="endDateTxt"
                                    name="endDateTxt"
                                    value={searchFormData.endDateTxt}
                                    onChange={handleEndDateChange}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => dispatchFormData({ endDateTxt: "" })}
                                                    size="small"
                                                    sx={{ padding: '4px' }}
                                                >
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover fieldset': {
                                                borderColor: '#5A73EA',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#5A73EA',
                                            },
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#5A73EA',
                                        },
                                        '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                            filter: 'invert(40%) sepia(24%) saturate(3168%) hue-rotate(207deg) brightness(102%) contrast(84%)',
                                        },
                                    }}
                                />
                            </FormControl>
                        </div>
                        
                        {/* Reset dates button */}
                        <Button 
                            variant="contained"
                            onClick={() => {
                                const today = getTodayDate();
                                dispatchFormData({ startDateTxt: today, endDateTxt: today, hasValidDateData: true });
                            }}
                            sx={{ 
                                backgroundColor: '#5A73EA',
                                '&:hover': {
                                    backgroundColor: '#4A63DA'
                                },
                                padding: '8px 16px',
                                minWidth: '80px',
                                fontWeight: '500',
                                flexShrink: 0
                            }}
                        >
                            RESET
                        </Button>
                        </div>
                        
                        {/* Case Search Field */}
                        <div style={{ 
                            flex: "1 1 220px", 
                            minWidth: "180px", 
                            maxWidth: "350px"
                        }}>
                            <TextFieldWithIconButton
                                ref={(node) => {
                                    caseSearchRefs.current[0] = node;
                                }}
                                nameForField="caseSearchTxt"
                                onIconButtonClick={handleCaseSearchClick}
                                onClearButtonClick={handleCaseSearchClear}
                                onTextChange={handleCaseSearchChange}
                                valueForField={searchFormData.caseSearchTxt}
                                labelForField="Case Search"
                                iconsTooltipText={{ search: 'Search Case', copy: 'Copy Case', clear: 'Clear Case' }}
                                iconsVisibility={{ search: false, copy: true, clear: true }}
                                iconsDisabled={caseNumberIconsDisabled}
                                sizeForControl="small"
                            />
                        </div>
                        
                        {/* Dropdowns Section - responsive container */}
                        <div style={{
                            display: "flex",
                            gap: "12px",
                            flex: "1 1 auto",
                            minWidth: "0",
                            flexWrap: "wrap"
                        }}>
                            {/* Four Dropdowns */}
                            <div ref={(el) => { workflowDropdownContainerRefs.current[0] = el; }} style={{ flex: "0 0 200px", width: "200px", minWidth: "200px", maxWidth: "200px" }}>
                            <CustomAutoComplete
                                customVariant='defaultVariant'
                                idForControl='workflowSelect'
                                labelForControl='Workflow'
                                multiple
                                disableCloseOnSelect={true}
                                valueForControl={searchFormData.workflowSelect}
                                onChange={handleWorkflowSelectChange}
                                optionsSource={workflowOptions}
                                labelKey='label'
                                valueKey='value'
                                open={workflowDropdownOpenByTab[0] ?? undefined}
                                onOpen={() => handleWorkflowDropdownOpen(0)}
                                onClose={() => handleWorkflowDropdownClose(0)}
                                renderOption={renderWorkflowOptionRow}
                                noOptionsText='Workflow Not Found'
                                renderTags={(tagValue, getTagProps) => {
                                    const total = workflowOptions.filter(o => o.value !== 'All').length;
                                    const count = tagValue.length;
                                    if (count === 0 || count === total) return <Chip label={`All (${total})`} color="primary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                    if (count === 1) return <Chip {...getTagProps({ index: 0 })} label={tagValue[0].label} style={{ backgroundColor: '#5A73EA' }} color="secondary" size="small" />;
                                    return <Chip label={`Multiple (${count})`} color="secondary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                }}
                            />
                        </div>
                        <div ref={(el) => { queueDropdownContainerRefs.current[0] = el; }} style={{ flex: "0 0 200px", width: "200px", minWidth: "200px", maxWidth: "200px" }}>
                            <CustomAutoComplete
                                customVariant='defaultVariant'
                                idForControl='queueSelect'
                                labelForControl='Queue'
                                multiple
                                disableCloseOnSelect={true}
                                valueForControl={searchFormData.queueSelect}
                                onChange={handleQueueSelectChange}
                                optionsSource={queueOptions}
                                labelKey='label'
                                valueKey='value'
                                open={queueDropdownOpenByTab[0] ?? undefined}
                                onOpen={() => handleQueueDropdownOpen(0)}
                                onClose={() => handleQueueDropdownClose(0)}
                                renderOption={renderQueueOptionRow}
                                noOptionsText='Queue Not Found'
                                renderTags={(tagValue, getTagProps) => {
                                    const total = queueOptions.filter(o => o.value !== 'All').length;
                                    const count = tagValue.length;
                                    if (count === 0 || count === total) return <Chip label={`All (${total})`} color="primary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                    if (count === 1) return <Chip {...getTagProps({ index: 0 })} label={tagValue[0].label} style={{ backgroundColor: '#5A73EA' }} color="secondary" size="small" />;
                                    return <Chip label={`Multiple (${count})`} color="secondary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                }}
                            />
                        </div>
                        <div style={{ flex: "0 0 200px", width: "200px", minWidth: "200px", maxWidth: "200px" }}>
                            <CustomAutoComplete
                                customVariant='defaultVariant'
                                idForControl='siteSelect'
                                labelForControl='Site'
                                multiple
                                disableCloseOnSelect={true}
                                valueForControl={searchFormData.siteSelect}
                                onChange={handleSiteSelectChange}
                                optionsSource={siteOptions}
                                labelKey='label'
                                valueKey='value'
                                renderOption={renderSiteOptionRow}
                                noOptionsText='Site Not Found'
                                renderTags={(tagValue, getTagProps) => {
                                    const total = siteOptions.filter(o => o.value !== 'All').length;
                                    const count = tagValue.length;
                                    if (count === 0 || count === total) return <Chip label={`All (${total})`} color="primary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                    if (count === 1) return <Chip {...getTagProps({ index: 0 })} label={tagValue[0].label} style={{ backgroundColor: '#5A73EA' }} color="secondary" size="small" />;
                                    return <Chip label={`Multiple (${count})`} color="secondary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                }}
                            />
                        </div>
                        {canSeeUserDropdown && (
                        <div style={{ flex: "0 0 200px", width: "200px", minWidth: "200px", maxWidth: "200px" }}>
                            <CustomAutoComplete
                                customVariant='defaultVariant'
                                idForControl='userSelect'
                                labelForControl='User'
                                multiple
                                disableCloseOnSelect={true}
                                valueForControl={searchFormData.userSelect}
                                onChange={handleUserSelectChange}
                                isDisabled={!canWriteUserDropdown}
                                optionsSource={visibleUserOptions}
                                labelKey='label'
                                valueKey='value'
                                renderOption={renderUserOptionRow}
                                noOptionsText='User Not Found'
                                renderTags={(tagValue, getTagProps) => {
                                    const total = visibleUserOptions.filter(o => o.value !== 'All').length;
                                    const count = tagValue.length;
                                    if (count === 0 || count === total) return <Chip label={`All (${total})`} color="primary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                    if (count === 1) return <Chip {...getTagProps({ index: 0 })} label={tagValue[0].label} style={{ backgroundColor: '#5A73EA' }} color="secondary" size="small" />;
                                    return <Chip label={`Multiple (${count})`} color="secondary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                }}
                            />
                        </div>
                        )}
                        </div>
                        
                        {/* Action buttons section - responsive */}
                        <div style={{ 
                            display: "flex", 
                            gap: "12px",
                            flexShrink: 0,
                            marginLeft: "auto",
                            minWidth: "fit-content"
                        }}>
                            {!(formFieldAccess.searchBtn?.hide ?? false) && (
                                <Button 
                                    id="searchBtn" 
                                    name="searchBtn" 
                                    variant="contained" 
                                    sx={{ 
                                        backgroundColor: '#131E42',            
                                        padding: '8px 24px',
                                        minWidth: '100px',
                                        fontWeight: '500'
                                    }} 
                                    onClick={handleSearchCompletedClick} 
                                    disabled={!(formFieldAccess.searchBtn?.write ?? true) || isCaseSearchInvalid}
                                >
                                    SEARCH
                                </Button>
                            )}
                            {!(formFieldAccess.clearFiltersBtn?.hide ?? false) && (
                                <Button 
                                    id="clearFiltersBtn" 
                                    name="clearFiltersBtn" 
                                    variant="text" 
                                    color="ywTextBlue"
                                    className="clear-filters-btn"
                                    onClick={handleGridClearFilters} 
                                    disabled={!(formFieldAccess.clearFiltersBtn?.write ?? true)}
                                >
                                    CLEAR FILTERS
                                </Button>
                            )}
                        </div>
                    </div>
                    {/* Dotted line separator */}
                    <div className="dotted-line-div" style={{ padding: "4px 14px" }}>
                        <div style={{ padding: "2px" }}>
                        </div>
                    </div>
                    {/* Grid Control Buttons Row - responsive layout */}
                    <div style={{ 
                        display: "flex", 
                        flexWrap: "wrap",
                        justifyContent: "space-between", 
                        alignItems: "center", 
                        padding: "8px 24px", 
                        backgroundColor: "white", 
                        gap: "12px"
                    }}>
                        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                            <Button id="completed-columns-button" variant="text" sx={gridToolbarButtonSx} onClick={handleOpenColumnsPanel} disabled={resultsCompleted.length === 0}>
                                <span style={{ marginRight: "6px", fontSize: "16px" }}>⋮⋮</span> COLUMNS
                            </Button>
                            <Button id="completed-filters-button" variant="text" sx={gridToolbarButtonSx} onClick={handleOpenFiltersPanel} disabled={resultsCompleted.length === 0}>
                                <span style={{ marginRight: "6px", fontSize: "16px" }}>⧨</span> FILTERS
                            </Button>
                            <Button id="completed-density-button" variant="text" sx={gridToolbarButtonSx} onClick={handleOpenDensityMenu} disabled={resultsCompleted.length === 0}>
                                <span style={{ marginRight: "6px", fontSize: "16px" }}>▦</span> DENSITY
                            </Button>
                            <Button
                                id="completed-export-button"
                                variant="text"
                                sx={gridToolbarButtonSx}
                                onClick={handleExportBtnClick}
                                disabled={resultsCompleted.length === 0 || !(formFieldAccess.exportDocumentsBtn?.write ?? true)}
                            >
                                <span style={{ marginRight: "6px", fontSize: "16px" }}>⤴</span> EXPORT
                            </Button>
                            <Button
                                id="completed-copy-button"
                                variant="text"
                                sx={gridToolbarButtonSx}
                                onClick={handleCopyAllWithHeaders}
                                disabled={resultsCompleted.length === 0}
                            >
                                <span style={{ marginRight: "6px", fontSize: "16px" }}>⧉</span> COPY
                            </Button>
                            <Menu
                                anchorEl={densityMenuAnchorEl}
                                open={Boolean(densityMenuAnchorEl)}
                                onClose={handleCloseDensityMenu}
                            >
                                <MenuItem selected={gridDensity === 'compact'} onClick={() => handleDensityChange('compact')}>Compact</MenuItem>
                                <MenuItem selected={gridDensity === 'standard'} onClick={() => handleDensityChange('standard')}>Standard</MenuItem>
                                <MenuItem selected={gridDensity === 'comfortable'} onClick={() => handleDensityChange('comfortable')}>Comfortable</MenuItem>
                            </Menu>
                        </div>
                        <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: "16px",
                            flexWrap: "wrap",
                            minWidth: "fit-content"
                        }}>
                            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "#5A73EA", fontWeight: 500 }}>
                                <input
                                    type="checkbox"
                                    style={{ accentColor: "#5A73EA" }}
                                    checked={includeSystemActions}
                                    onChange={handleIncludeSystemActionsChange}
                                />
                                <span>INCLUDE SYSTEM ACTIONS</span>
                            </label>
                            {canSeeQAButton && (
                                <Button
                                    id="qaBtn"
                                    name="qaBtn"
                                    variant="contained"
                                    sx={{ backgroundColor: '#131E42', fontWeight: '500' }}
                                    onClick={handleStartQAWorkflowClick}
                                    disabled={!canWriteQAButton || qaCheckedRowIds.size === 0}
                                >
                                    QA
                                </Button>
                            )}
                        </div>
                    </div>
                    {!(formFieldAccess.completedWorkflowActivityDgrd?.hide ?? false) && (
                        !hasSearched ? (
                            <div style={completedEmptyStateStyle}>
                                <div style={{ fontSize: '18px', fontWeight: '525', color: '#5A73EA' }}>
                                    Don't worry. Use the filters!
                                </div>
                                <div style={{ fontSize: '16px', color: '#5A73EA', maxWidth: '450px' }}>
                                    Utilize the various filters above and search for the information you seek.
                                </div>
                            </div>
                        ) : resultsCompleted.length === 0 ? (
                            <div style={completedEmptyStateStyle}>
                                <div style={{ fontSize: '16px', color: '#5A73EA', maxWidth: '450px' }}>
                                    No results found.
                                </div>
                            </div>
                        ) : (
                            <div style={completedGridContainerStyle}>
                                <DataGridPremium
                                    apiRef={workflowGridApiRef}
                                    id="completedWorkflowActivityDgrd"
                                    name="completedWorkflowActivityDgrd"
                                    rows={resultsCompleted}
                                    columns={currentColumns}
                                    density={gridDensity}
                                    pagination
                                    pageSizeOptions={[1000, 2000, 3000, 4000, 5000]}
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={setPaginationModel}
                                    sortModel={sortModel}
                                    onSortModelChange={setSortModel}
                                    columnVisibilityModel={columnVisibilityModel}
                                    onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                                    filterModel={gridFilterModel}
                                    onRowDoubleClick={handleRowDoubleClick}
                                    onFilterModelChange={handleGridFilterChange}
                                    onKeyDown={handleGridKeyDown}
                                    onColumnOrderChange={handleGridColumnOrderChange}
                                    onColumnWidthChange={handleColumnWidthChange}
                                    onPinnedColumnsChange={handlePinnedColumnsChange}
                                    disableMultipleColumnsFiltering={false}
                                    cellSelection
                                    initialState={savedGridStateByTab.current[0] || undefined}
                                    slots={{ footer: CompletedGridFooter }}
                                    sx={completedGridSx}
                                />
                            </div>
                        )
                    )}
                </div>
            </Box>
        );
    };

    const renderQAForm = (activeForm) => {
        return (
            <Box
                sx={{
                    display: activeForm ? 'block' : 'none'
                }}
            >
                <div style={{ width: "100%" }}>
                    {/* Filter Row - responsive layout for different screen sizes */}
                    <div style={{ 
                        display: "flex", 
                        flexWrap: "wrap",
                        alignItems: "center", 
                        gap: "12px", 
                        padding: "16px 24px", 
                        backgroundColor: "#ffffffff", 
                        minHeight: "70px"
                    }} onKeyDown={handleFilterKeyDown}>
                        {/* Date Section - responsive container */}
                        <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: "8px",
                            flexWrap: "nowrap",
                            flexShrink: 1,
                            minWidth: "0"
                        }}>
                            {/* Start Date Field */}
                            <div style={{ flex: "0 1 220px", minWidth: "150px", maxWidth: "306px" }}>
                                <FormControl fullWidth>
                                <TextField 
                                    label="Start Date" 
                                    type="date" 
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: getTodayDate() }}
                                    size='small'
                                    id="startDateTxt"
                                    name="startDateTxt"
                                    value={searchFormData.startDateTxt}
                                    onChange={handleStartDateChange}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => dispatchFormData({ startDateTxt: "" })}
                                                    size="small"
                                                    sx={{ padding: '4px' }}
                                                >
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover fieldset': {
                                                borderColor: '#5A73EA',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#5A73EA',
                                            },
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#5A73EA',
                                        },
                                        '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                            filter: 'invert(40%) sepia(24%) saturate(3168%) hue-rotate(207deg) brightness(102%) contrast(84%)',
                                        },
                                    }}
                                />
                            </FormControl>
                        </div>
                        
                        
                        {/* End Date Field */}
                        <div style={{ flex: "0 1 220px", minWidth: "150px", maxWidth: "306px" }}>
                            <FormControl fullWidth>
                                <TextField 
                                    label="End Date" 
                                    type="date" 
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: getTodayDate() }}
                                    size='small'
                                    id="endDateTxt"
                                    name="endDateTxt"
                                    value={searchFormData.endDateTxt}
                                    onChange={handleEndDateChange}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => dispatchFormData({ endDateTxt: "" })}
                                                    size="small"
                                                    sx={{ padding: '4px' }}
                                                >
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover fieldset': {
                                                borderColor: '#5A73EA',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#5A73EA',
                                            },
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#5A73EA',
                                        },
                                        '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                            filter: 'invert(40%) sepia(24%) saturate(3168%) hue-rotate(207deg) brightness(102%) contrast(84%)',
                                        },
                                    }}
                                />
                            </FormControl>
                        </div>
                        
                        {/* Reset dates button */}
                        <Button 
                            variant="contained"
                            onClick={() => {
                                const today = getTodayDate();
                                dispatchFormData({ startDateTxt: today, endDateTxt: today, hasValidDateData: true });
                            }}
                            sx={{ 
                                backgroundColor: '#5A73EA',
                                '&:hover': {
                                    backgroundColor: '#4A63DA'
                                },
                                padding: '8px 16px',
                                minWidth: '80px',
                                fontWeight: '500',
                                flexShrink: 0
                            }}
                        >
                            RESET
                        </Button>
                        </div>
                        
                        {/* Case Search Field */}
                        <div style={{ 
                            flex: "1 1 220px", 
                            minWidth: "180px", 
                            maxWidth: "350px"
                        }}>
                            <TextFieldWithIconButton
                                ref={(node) => {
                                    caseSearchRefs.current[1] = node;
                                }}
                                nameForField="caseSearchTxt"
                                onIconButtonClick={handleCaseSearchClick}
                                onClearButtonClick={handleCaseSearchClear}
                                onTextChange={handleCaseSearchChange}
                                valueForField={searchFormData.caseSearchTxt}
                                labelForField="Case Search"
                                iconsTooltipText={{ search: 'Search Case', copy: 'Copy Case', clear: 'Clear Case' }}
                                iconsVisibility={{ search: false, copy: true, clear: true }}
                                iconsDisabled={caseNumberIconsDisabled}
                                sizeForControl="small"
                            />
                        </div>
                        
                        {/* Dropdowns Section - responsive container */}
                        <div style={{
                            display: "flex",
                            gap: "12px",
                            flex: "1 1 auto",
                            minWidth: "0",
                            flexWrap: "wrap"
                        }}>
                            {/* Four Dropdowns */}
                            <div ref={(el) => { workflowDropdownContainerRefs.current[1] = el; }} style={{ flex: "0 0 200px", width: "200px", minWidth: "200px", maxWidth: "200px" }}>
                            <CustomAutoComplete
                                customVariant='defaultVariant'
                                idForControl='workflowSelect'
                                labelForControl='Workflow'
                                multiple
                                disableCloseOnSelect={true}
                                valueForControl={searchFormData.workflowSelect}
                                onChange={handleWorkflowSelectChange}
                                optionsSource={workflowOptions}
                                labelKey='label'
                                valueKey='value'
                                open={workflowDropdownOpenByTab[1] ?? undefined}
                                onOpen={() => handleWorkflowDropdownOpen(1)}
                                onClose={() => handleWorkflowDropdownClose(1)}
                                renderOption={renderWorkflowOptionRow}
                                noOptionsText='Workflow Not Found'
                                renderTags={(tagValue, getTagProps) => {
                                    const total = workflowOptions.filter(o => o.value !== 'All').length;
                                    const count = tagValue.length;
                                    if (count === 0 || count === total) return <Chip label={`All (${total})`} color="primary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                    if (count === 1) return <Chip {...getTagProps({ index: 0 })} label={tagValue[0].label} style={{ backgroundColor: '#5A73EA' }} color="secondary" size="small" />;
                                    return <Chip label={`Multiple (${count})`} color="secondary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                }}
                            />
                        </div>
                        <div ref={(el) => { queueDropdownContainerRefs.current[1] = el; }} style={{ flex: "0 0 200px", width: "200px", minWidth: "200px", maxWidth: "200px" }}>
                            <CustomAutoComplete
                                customVariant='defaultVariant'
                                idForControl='queueSelect'
                                labelForControl='Queue'
                                multiple
                                disableCloseOnSelect={true}
                                valueForControl={searchFormData.queueSelect}
                                onChange={handleQueueSelectChange}
                                optionsSource={queueOptions}
                                labelKey='label'
                                valueKey='value'
                                open={queueDropdownOpenByTab[1] ?? undefined}
                                onOpen={() => handleQueueDropdownOpen(1)}
                                onClose={() => handleQueueDropdownClose(1)}
                                renderOption={renderQueueOptionRow}
                                noOptionsText='Queue Not Found'
                                renderTags={(tagValue, getTagProps) => {
                                    const total = queueOptions.filter(o => o.value !== 'All').length;
                                    const count = tagValue.length;
                                    if (count === 0 || count === total) return <Chip label={`All (${total})`} color="primary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                    if (count === 1) return <Chip {...getTagProps({ index: 0 })} label={tagValue[0].label} style={{ backgroundColor: '#5A73EA' }} color="secondary" size="small" />;
                                    return <Chip label={`Multiple (${count})`} color="secondary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                }}
                            />
                        </div>
                        <div style={{ flex: "0 0 200px", width: "200px", minWidth: "200px", maxWidth: "200px" }}>
                            <CustomAutoComplete
                                customVariant='defaultVariant'
                                idForControl='siteSelect'
                                labelForControl='Site'
                                multiple
                                disableCloseOnSelect={true}
                                valueForControl={searchFormData.siteSelect}
                                onChange={handleSiteSelectChange}
                                optionsSource={siteOptions}
                                labelKey='label'
                                valueKey='value'
                                renderOption={renderSiteOptionRow}
                                noOptionsText='Site Not Found'
                                renderTags={(tagValue, getTagProps) => {
                                    const total = siteOptions.filter(o => o.value !== 'All').length;
                                    const count = tagValue.length;
                                    if (count === 0 || count === total) return <Chip label={`All (${total})`} color="primary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                    if (count === 1) return <Chip {...getTagProps({ index: 0 })} label={tagValue[0].label} style={{ backgroundColor: '#5A73EA' }} color="secondary" size="small" />;
                                    return <Chip label={`Multiple (${count})`} color="secondary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                }}
                            />
                        </div>
                        {canSeeUserDropdown && (
                        <div style={{ flex: "0 0 200px", width: "200px", minWidth: "200px", maxWidth: "200px" }}>
                            <CustomAutoComplete
                                customVariant='defaultVariant'
                                idForControl='userSelect'
                                labelForControl='User'
                                multiple
                                disableCloseOnSelect={true}
                                valueForControl={searchFormData.userSelect}
                                onChange={handleUserSelectChange}
                                isDisabled={!canWriteUserDropdown}
                                optionsSource={visibleUserOptions}
                                labelKey='label'
                                valueKey='value'
                                renderOption={renderUserOptionRow}
                                noOptionsText='User Not Found'
                                renderTags={(tagValue, getTagProps) => {
                                    const total = visibleUserOptions.filter(o => o.value !== 'All').length;
                                    const count = tagValue.length;
                                    if (count === 0 || count === total) return <Chip label={`All (${total})`} color="primary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                    if (count === 1) return <Chip {...getTagProps({ index: 0 })} label={tagValue[0].label} style={{ backgroundColor: '#5A73EA' }} color="secondary" size="small" />;
                                    return <Chip label={`Multiple (${count})`} color="secondary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                }}
                            />
                        </div>
                        )}
                        </div>
                        
                        {/* Action buttons section - responsive */}
                        <div style={{ 
                            display: "flex", 
                            gap: "12px",
                            flexShrink: 0,
                            marginLeft: "auto",
                            minWidth: "fit-content"
                        }}>
                            {!(formFieldAccess.searchBtn?.hide ?? false) && (
                                <Button 
                                    id="searchBtn" 
                                    name="searchBtn" 
                                    variant="contained" 
                                    sx={{ 
                                        backgroundColor: '#131E42',            
                                        padding: '8px 24px',
                                        minWidth: '100px',
                                        fontWeight: '500'
                                    }} 
                                    onClick={handleSearchCompletedClick} 
                                    disabled={!(formFieldAccess.searchBtn?.write ?? true) || isCaseSearchInvalid}
                                >
                                    SEARCH
                                </Button>
                            )}
                           
                            {!(formFieldAccess.clearFiltersBtn?.hide ?? false) && (
                                <Button 
                                    id="clearFiltersBtn" 
                                    name="clearFiltersBtn" 
                                    variant="text" 
                                    color="ywTextBlue"
                                    className="clear-filters-btn"
                                    onClick={handleGridClearFilters} 
                                    disabled={!(formFieldAccess.clearFiltersBtn?.write ?? true)}
                                >
                                    CLEAR FILTERS
                                </Button>
                            )}
                        </div>
                    </div>
                    {/* Dotted line separator */}
                    <div className="dotted-line-div" style={{ padding: "4px 14px" }}>
                        <div style={{ padding: "2px" }}>
                        </div>
                    </div>
                    {/* Grid Control Buttons Row - responsive layout */}
                    <div style={{ 
                        display: "flex", 
                        flexWrap: "wrap",
                        justifyContent: "space-between", 
                        alignItems: "center", 
                        padding: "8px 24px", 
                        backgroundColor: "white", 
                        gap: "12px"
                    }}>
                        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                            <Button id="completed-columns-button" variant="text" sx={gridToolbarButtonSx} onClick={handleOpenColumnsPanel} disabled={resultsCompleted.length === 0}>
                                <span style={{ marginRight: "6px", fontSize: "16px" }}>⋮⋮</span> COLUMNS
                            </Button>
                            <Button id="completed-filters-button" variant="text" sx={gridToolbarButtonSx} onClick={handleOpenFiltersPanel} disabled={resultsCompleted.length === 0}>
                                <span style={{ marginRight: "6px", fontSize: "16px" }}>⧨</span> FILTERS
                            </Button>
                            <Button id="completed-density-button" variant="text" sx={gridToolbarButtonSx} onClick={handleOpenDensityMenu} disabled={resultsCompleted.length === 0}>
                                <span style={{ marginRight: "6px", fontSize: "16px" }}>▦</span> DENSITY
                            </Button>
                            <Button
                                id="completed-export-button"
                                variant="text"
                                sx={gridToolbarButtonSx}
                                onClick={handleExportBtnClick}
                                disabled={resultsCompleted.length === 0 || !(formFieldAccess.exportDocumentsBtn?.write ?? true)}
                            >
                                <span style={{ marginRight: "6px", fontSize: "16px" }}>⤴</span> EXPORT
                            </Button>
                            <Button
                                id="completed-copy-button"
                                variant="text"
                                sx={gridToolbarButtonSx}
                                onClick={handleCopyAllWithHeaders}
                                disabled={resultsCompleted.length === 0}
                            >
                                <span style={{ marginRight: "6px", fontSize: "16px" }}>⧉</span> COPY
                            </Button>
                            <Menu
                                anchorEl={densityMenuAnchorEl}
                                open={Boolean(densityMenuAnchorEl)}
                                onClose={handleCloseDensityMenu}
                            >
                                <MenuItem selected={gridDensity === 'compact'} onClick={() => handleDensityChange('compact')}>Compact</MenuItem>
                                <MenuItem selected={gridDensity === 'standard'} onClick={() => handleDensityChange('standard')}>Standard</MenuItem>
                                <MenuItem selected={gridDensity === 'comfortable'} onClick={() => handleDensityChange('comfortable')}>Comfortable</MenuItem>
                            </Menu>
                        </div>
                        <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: "16px",
                            flexWrap: "wrap",
                            minWidth: "fit-content"
                        }}>
                            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "#5A73EA", fontWeight: 500 }}>
                                <input
                                    type="checkbox"
                                    style={{ accentColor: "#5A73EA" }}
                                    checked={includeSystemActions}
                                    onChange={handleIncludeSystemActionsChange}
                                />
                                <span>INCLUDE SYSTEM ACTIONS</span>
                            </label>
                            {canSeeQAButton && (
                                <Button
                                    id="qaBtn"
                                    name="qaBtn"
                                    variant="contained"
                                    sx={{ backgroundColor: '#131E42', fontWeight: '500' }}
                                    onClick={handleStartQAWorkflowClick}
                                    disabled={!canWriteQAButton || qaCheckedRowIds.size === 0}
                                >
                                    QA
                                </Button>
                            )}
                        </div>
                    </div>
                    {!(formFieldAccess.completedWorkflowActivityDgrd?.hide ?? false) && (
                        !hasSearched ? (
                            <div style={completedEmptyStateStyle}>
                                <div style={{ fontSize: '18px', fontWeight: '525', color: '#5A73EA' }}>
                                    Don't worry. Use the filters!
                                </div>
                                <div style={{ fontSize: '16px', color: '#5A73EA', maxWidth: '450px' }}>
                                    Utilize the various filters above and search for the information you seek.
                                </div>
                            </div>
                        ) : resultsCompleted.length === 0 ? (
                            <div style={completedEmptyStateStyle}>
                                <div style={{ fontSize: '16px', color: '#5A73EA', maxWidth: '450px' }}>
                                    No results found.
                                </div>
                            </div>
                        ) : (
                            <div style={completedGridContainerStyle}>
                                <DataGridPremium
                                    apiRef={qaGridApiRef}
                                    id="completedWorkflowActivityDgrd"
                                    name="completedWorkflowActivityDgrd"
                                    rows={resultsCompleted}
                                    columns={currentColumns}
                                    density={gridDensity}
                                    pagination
                                    pageSizeOptions={[1000, 2000, 3000, 4000, 5000]}
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={setPaginationModel}
                                    sortModel={sortModel}
                                    onSortModelChange={setSortModel}
                                    columnVisibilityModel={columnVisibilityModel}
                                    onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                                    filterModel={gridFilterModel}
                                    onRowDoubleClick={handleRowDoubleClick}
                                    onFilterModelChange={handleGridFilterChange}
                                    onKeyDown={handleGridKeyDown}
                                    onColumnOrderChange={handleGridColumnOrderChange}
                                    onColumnWidthChange={handleColumnWidthChange}
                                    onPinnedColumnsChange={handlePinnedColumnsChange}
                                    disableMultipleColumnsFiltering={false}
                                    cellSelection
                                    initialState={savedGridStateByTab.current[1] || undefined}
                                    slots={{ footer: CompletedGridFooter }}
                                    sx={completedGridSx}
                                />
                            </div>
                        )
                    )}
                </div>
            </Box>
        );
    };

    const renderVarianceForm = (activeForm) => {
        return (
            <Box
                sx={{
                    display: activeForm ? 'block' : 'none'
                }}
            >
                <div style={{ width: "100%" }}>
                    {/* Filter Row - responsive layout for different screen sizes */}
                    <div style={{ 
                        display: "flex", 
                        flexWrap: "wrap",
                        alignItems: "center", 
                        gap: "12px", 
                        padding: "16px 24px", 
                        backgroundColor: "#ffffffff", 
                        minHeight: "70px"
                    }} onKeyDown={handleFilterKeyDown}>
                        {/* Date Section - responsive container */}
                        <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: "8px",
                            flexWrap: "nowrap",
                            flexShrink: 1,
                            minWidth: "0"
                        }}>
                            {/* Start Date Field */}
                            <div style={{ flex: "0 1 220px", minWidth: "150px", maxWidth: "306px" }}>
                                <FormControl fullWidth>
                                <TextField 
                                    label="Start Date" 
                                    type="date" 
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: getTodayDate() }}
                                    size='small'
                                    id="startDateTxt"
                                    name="startDateTxt"
                                    value={searchFormData.startDateTxt}
                                    onChange={handleStartDateChange}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => dispatchFormData({ startDateTxt: "" })}
                                                    size="small"
                                                    sx={{ padding: '4px' }}
                                                >
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover fieldset': {
                                                borderColor: '#5A73EA',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#5A73EA',
                                            },
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#5A73EA',
                                        },
                                        '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                            filter: 'invert(40%) sepia(24%) saturate(3168%) hue-rotate(207deg) brightness(102%) contrast(84%)',
                                        },
                                    }}
                                />
                            </FormControl>
                        </div>
                        
                        
                        {/* End Date Field */}
                        <div style={{ flex: "0 1 220px", minWidth: "150px", maxWidth: "306px" }}>
                            <FormControl fullWidth>
                                <TextField 
                                    label="End Date" 
                                    type="date" 
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: getTodayDate() }}
                                    size='small'
                                    id="endDateTxt"
                                    name="endDateTxt"
                                    value={searchFormData.endDateTxt}
                                    onChange={handleEndDateChange}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => dispatchFormData({ endDateTxt: "" })}
                                                    size="small"
                                                    sx={{ padding: '4px' }}
                                                >
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:hover fieldset': {
                                                borderColor: '#5A73EA',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#5A73EA',
                                            },
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#5A73EA',
                                        },
                                        '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                            filter: 'invert(40%) sepia(24%) saturate(3168%) hue-rotate(207deg) brightness(102%) contrast(84%)',
                                        },
                                    }}
                                />
                            </FormControl>
                        </div>
                        
                        {/* Reset dates button */}
                        <Button 
                            variant="contained"
                            onClick={() => {
                                const today = getTodayDate();
                                dispatchFormData({ startDateTxt: today, endDateTxt: today, hasValidDateData: true });
                            }}
                            sx={{ 
                                backgroundColor: '#5A73EA',
                                '&:hover': {
                                    backgroundColor: '#4A63DA'
                                },
                                padding: '8px 16px',
                                minWidth: '80px',
                                fontWeight: '500',
                                flexShrink: 0
                            }}
                        >
                            RESET
                        </Button>
                        </div>
                        
                        {/* Case Search Field */}
                        <div style={{ 
                            flex: "1 1 220px", 
                            minWidth: "180px", 
                            maxWidth: "350px"
                        }}>
                            <TextFieldWithIconButton
                                ref={(node) => {
                                    caseSearchRefs.current[2] = node;
                                }}
                                nameForField="caseSearchTxt"
                                onIconButtonClick={handleCaseSearchClick}
                                onClearButtonClick={handleCaseSearchClear}
                                onTextChange={handleCaseSearchChange}
                                valueForField={searchFormData.caseSearchTxt}
                                labelForField="Case Search"
                                iconsTooltipText={{ search: 'Search Case', copy: 'Copy Case', clear: 'Clear Case' }}
                                iconsVisibility={{ search: false, copy: true, clear: true }}
                                iconsDisabled={caseNumberIconsDisabled}
                                sizeForControl="small"
                            />
                        </div>
                        
                        {/* Dropdowns Section - responsive container */}
                        <div style={{
                            display: "flex",
                            gap: "12px",
                            flex: "1 1 auto",
                            minWidth: "0",
                            flexWrap: "wrap"
                        }}>
                            {/* Four Dropdowns */}
                            <div ref={(el) => { workflowDropdownContainerRefs.current[2] = el; }} style={{ flex: "0 0 200px", width: "200px", minWidth: "200px", maxWidth: "200px" }}>
                            <CustomAutoComplete
                                customVariant='defaultVariant'
                                idForControl='workflowSelect'
                                labelForControl='Workflow'
                                multiple
                                disableCloseOnSelect={true}
                                valueForControl={searchFormData.workflowSelect}
                                onChange={handleWorkflowSelectChange}
                                optionsSource={workflowOptions}
                                labelKey='label'
                                valueKey='value'
                                open={workflowDropdownOpenByTab[2] ?? undefined}
                                onOpen={() => handleWorkflowDropdownOpen(2)}
                                onClose={() => handleWorkflowDropdownClose(2)}
                                renderOption={renderWorkflowOptionRow}
                                noOptionsText='Workflow Not Found'
                                renderTags={(tagValue, getTagProps) => {
                                    const total = workflowOptions.filter(o => o.value !== 'All').length;
                                    const count = tagValue.length;
                                    if (count === 0 || count === total) return <Chip label={`All (${total})`} color="primary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                    if (count === 1) return <Chip {...getTagProps({ index: 0 })} label={tagValue[0].label} style={{ backgroundColor: '#5A73EA' }} color="secondary" size="small" />;
                                    return <Chip label={`Multiple (${count})`} color="secondary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                }}
                            />
                        </div>
                        <div ref={(el) => { queueDropdownContainerRefs.current[2] = el; }} style={{ flex: "0 0 200px", width: "200px", minWidth: "200px", maxWidth: "200px" }}>
                            <CustomAutoComplete
                                customVariant='defaultVariant'
                                idForControl='queueSelect'
                                labelForControl='Queue'
                                multiple
                                disableCloseOnSelect={true}
                                valueForControl={searchFormData.queueSelect}
                                onChange={handleQueueSelectChange}
                                optionsSource={queueOptions}
                                labelKey='label'
                                valueKey='value'
                                open={queueDropdownOpenByTab[2] ?? undefined}
                                onOpen={() => handleQueueDropdownOpen(2)}
                                onClose={() => handleQueueDropdownClose(2)}
                                renderOption={renderQueueOptionRow}
                                noOptionsText='Queue Not Found'
                                renderTags={(tagValue, getTagProps) => {
                                    const total = queueOptions.filter(o => o.value !== 'All').length;
                                    const count = tagValue.length;
                                    if (count === 0 || count === total) return <Chip label={`All (${total})`} color="primary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                    if (count === 1) return <Chip {...getTagProps({ index: 0 })} label={tagValue[0].label} style={{ backgroundColor: '#5A73EA' }} color="secondary" size="small" />;
                                    return <Chip label={`Multiple (${count})`} color="secondary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                }}
                            />
                        </div>
                        <div style={{ flex: "0 0 200px", width: "200px", minWidth: "200px", maxWidth: "200px" }}>
                            <CustomAutoComplete
                                customVariant='defaultVariant'
                                idForControl='siteSelect'
                                labelForControl='Site'
                                multiple
                                disableCloseOnSelect={true}
                                valueForControl={searchFormData.siteSelect}
                                onChange={handleSiteSelectChange}
                                optionsSource={siteOptions}
                                labelKey='label'
                                valueKey='value'
                                renderOption={renderSiteOptionRow}
                                noOptionsText='Site Not Found'
                                renderTags={(tagValue, getTagProps) => {
                                    const total = siteOptions.filter(o => o.value !== 'All').length;
                                    const count = tagValue.length;
                                    if (count === 0 || count === total) return <Chip label={`All (${total})`} color="primary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                    if (count === 1) return <Chip {...getTagProps({ index: 0 })} label={tagValue[0].label} style={{ backgroundColor: '#5A73EA' }} color="secondary" size="small" />;
                                    return <Chip label={`Multiple (${count})`} color="secondary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                }}
                            />
                        </div>
                        {canSeeUserDropdown && (
                        <div style={{ flex: "0 0 200px", width: "200px", minWidth: "200px", maxWidth: "200px" }}>
                            <CustomAutoComplete
                                customVariant='defaultVariant'
                                idForControl='userSelect'
                                labelForControl='User'
                                multiple
                                disableCloseOnSelect={true}
                                valueForControl={searchFormData.userSelect}
                                onChange={handleUserSelectChange}
                                isDisabled={!canWriteUserDropdown}
                                optionsSource={visibleUserOptions}
                                labelKey='label'
                                valueKey='value'
                                renderOption={renderUserOptionRow}
                                noOptionsText='User Not Found'
                                renderTags={(tagValue, getTagProps) => {
                                    const total = visibleUserOptions.filter(o => o.value !== 'All').length;
                                    const count = tagValue.length;
                                    if (count === 0 || count === total) return <Chip label={`All (${total})`} color="primary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                    if (count === 1) return <Chip {...getTagProps({ index: 0 })} label={tagValue[0].label} style={{ backgroundColor: '#5A73EA' }} color="secondary" size="small" />;
                                    return <Chip label={`Multiple (${count})`} color="secondary" style={{ backgroundColor: '#5A73EA' }} size="small" />;
                                }}
                            />
                        </div>
                        )}
                        </div>
                        
                        {/* Action buttons section - responsive */}
                        <div style={{ 
                            display: "flex", 
                            gap: "12px",
                            flexShrink: 0,
                            marginLeft: "auto",
                            minWidth: "fit-content"
                        }}>
                            {!(formFieldAccess.searchBtn?.hide ?? false) && (
                                <Button 
                                    id="searchBtn" 
                                    name="searchBtn" 
                                    variant="contained" 
                                    sx={{ 
                                        backgroundColor: '#131E42',            
                                        padding: '8px 24px',
                                        minWidth: '100px',
                                        fontWeight: '500'
                                    }} 
                                    onClick={handleSearchCompletedClick} 
                                    disabled={!(formFieldAccess.searchBtn?.write ?? true) || isCaseSearchInvalid}
                                >
                                    SEARCH
                                </Button>
                            )}
                           
                            {!(formFieldAccess.clearFiltersBtn?.hide ?? false) && (
                                <Button 
                                    id="clearFiltersBtn" 
                                    name="clearFiltersBtn" 
                                    variant="text" 
                                    color="ywTextBlue"
                                    className="clear-filters-btn"
                                    onClick={handleGridClearFilters} 
                                    disabled={!(formFieldAccess.clearFiltersBtn?.write ?? true)}
                                >
                                    CLEAR FILTERS
                                </Button>
                            )}
                        </div>
                    </div>
                    {/* Dotted line separator */}
                    <div className="dotted-line-div" style={{ padding: "4px 14px" }}>
                        <div style={{ padding: "2px" }}>
                        </div>
                    </div>
                    {/* Grid Control Buttons Row - responsive layout */}
                    <div style={{ 
                        display: "flex", 
                        flexWrap: "wrap",
                        justifyContent: "space-between", 
                        alignItems: "center", 
                        padding: "8px 24px", 
                        backgroundColor: "white", 
                        gap: "12px"
                    }}>
                        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                            <Button id="completed-columns-button" variant="text" sx={gridToolbarButtonSx} onClick={handleOpenColumnsPanel} disabled={resultsCompleted.length === 0}>
                                <span style={{ marginRight: "6px", fontSize: "16px" }}>⋮⋮</span> COLUMNS
                            </Button>
                            <Button id="completed-filters-button" variant="text" sx={gridToolbarButtonSx} onClick={handleOpenFiltersPanel} disabled={resultsCompleted.length === 0}>
                                <span style={{ marginRight: "6px", fontSize: "16px" }}>⧨</span> FILTERS
                            </Button>
                            <Button id="completed-density-button" variant="text" sx={gridToolbarButtonSx} onClick={handleOpenDensityMenu} disabled={resultsCompleted.length === 0}>
                                <span style={{ marginRight: "6px", fontSize: "16px" }}>▦</span> DENSITY
                            </Button>
                            <Button
                                id="completed-export-button"
                                variant="text"
                                sx={gridToolbarButtonSx}
                                onClick={handleExportBtnClick}
                                disabled={resultsCompleted.length === 0 || !(formFieldAccess.exportDocumentsBtn?.write ?? true)}
                            >
                                <span style={{ marginRight: "6px", fontSize: "16px" }}>⤴</span> EXPORT
                            </Button>
                            <Button
                                id="completed-copy-button"
                                variant="text"
                                sx={gridToolbarButtonSx}
                                onClick={handleCopyAllWithHeaders}
                                disabled={resultsCompleted.length === 0}
                            >
                                <span style={{ marginRight: "6px", fontSize: "16px" }}>⧉</span> COPY
                            </Button>
                            <Menu
                                anchorEl={densityMenuAnchorEl}
                                open={Boolean(densityMenuAnchorEl)}
                                onClose={handleCloseDensityMenu}
                            >
                                <MenuItem selected={gridDensity === 'compact'} onClick={() => handleDensityChange('compact')}>Compact</MenuItem>
                                <MenuItem selected={gridDensity === 'standard'} onClick={() => handleDensityChange('standard')}>Standard</MenuItem>
                                <MenuItem selected={gridDensity === 'comfortable'} onClick={() => handleDensityChange('comfortable')}>Comfortable</MenuItem>
                            </Menu>
                        </div>
                        <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: "16px",
                            flexWrap: "wrap",
                            minWidth: "fit-content"
                        }}>
                            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "#5A73EA", fontWeight: 500 }}>
                                <input
                                    type="checkbox"
                                    style={{ accentColor: "#5A73EA" }}
                                    checked={includeSystemActions}
                                    onChange={handleIncludeSystemActionsChange}
                                />
                                <span>INCLUDE SYSTEM ACTIONS</span>
                            </label>
                            {canSeeQAButton && (
                                <Button
                                    id="qaBtn"
                                    name="qaBtn"
                                    variant="contained"
                                    sx={{ backgroundColor: '#131E42', fontWeight: '500' }}
                                    onClick={handleStartQAWorkflowClick}
                                    disabled={!canWriteQAButton || qaCheckedRowIds.size === 0}
                                >
                                    QA
                                </Button>
                            )}
                        </div>
                    </div>
                    {!(formFieldAccess.completedWorkflowActivityDgrd?.hide ?? false) && (
                        !hasSearched ? (
                            <div style={completedEmptyStateStyle}>
                                <div style={{ fontSize: '18px', fontWeight: '525', color: '#5A73EA' }}>
                                    Don't worry. Use the filters!
                                </div>
                                <div style={{ fontSize: '16px', color: '#5A73EA', maxWidth: '450px' }}>
                                    Utilize the various filters above and search for the information you seek.
                                </div>
                            </div>
                        ) : resultsCompleted.length === 0 ? (
                            <div style={completedEmptyStateStyle}>
                                <div style={{ fontSize: '16px', color: '#5A73EA', maxWidth: '450px' }}>
                                    No results found.
                                </div>
                            </div>
                        ) : (
                            <div style={completedGridContainerStyle}>
                                <DataGridPremium
                                    apiRef={varianceGridApiRef}
                                    id="completedWorkflowActivityDgrd"
                                    name="completedWorkflowActivityDgrd"
                                    rows={resultsCompleted}
                                    columns={currentColumns}
                                    density={gridDensity}
                                    pagination
                                    pageSizeOptions={[1000, 2000, 3000, 4000, 5000]}
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={setPaginationModel}
                                    sortModel={sortModel}
                                    onSortModelChange={setSortModel}
                                    columnVisibilityModel={columnVisibilityModel}
                                    onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                                    filterModel={gridFilterModel}
                                    onRowDoubleClick={handleRowDoubleClick}
                                    onFilterModelChange={handleGridFilterChange}
                                    onKeyDown={handleGridKeyDown}
                                    onColumnOrderChange={handleGridColumnOrderChange}
                                    onColumnWidthChange={handleColumnWidthChange}
                                    onPinnedColumnsChange={handlePinnedColumnsChange}
                                    disableMultipleColumnsFiltering={false}
                                    cellSelection
                                    initialState={savedGridStateByTab.current[2] || undefined}
                                    slots={{ footer: CompletedGridFooter }}
                                    sx={completedGridSx}
                                />
                            </div>
                        )
                    )}
                </div>
            </Box>
        );
    };

    const mainCompletedContent = (
        <div style={{ height: '100%', width: '100%', paddingTop: '40px', position: 'absolute' }}>
            <div className="flex-main-frame" >
                <div className="content-main-container" style={{ width: '100%' }}>
                    <div className="pane-wrap-container">
                        <div className="pane-main-full-width-container" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
                            <div className="form-section" style={{ height: 'auto', minHeight: 'calc(100% - 20px)' }}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    sx={{
                                        width: '100%'
                                    }}
                                >
                                    <div className="text-wrapper-completed-title" style={{ fontSize: '20px', fontWeight: 300, color: 'black' }}>Completed</div>
                                    <Tabs
                                        value={activeTab}
                                        onChange={handleTabChange}
                                        aria-label="completed tabs"
                                        sx={{
                                            "& .MuiTab-root": {
                                                fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                                                fontWeight: 500,
                                                color: "rgba(0, 0, 0, 0.6)",
                                                "&.Mui-selected": {
                                                    color: "#5A73EA"
                                                },
                                                "&.Mui-disabled": {
                                                    color: "rgba(0, 0, 0, 0.3)"
                                                }
                                            },
                                            "& .MuiTabs-indicator": {
                                                backgroundColor: "#5A73EA"
                                            }
                                        }}
                                    >
                                        {!(formFieldAccess.btnCompletedWorkflow?.hide ?? false) && (
                                        <Tab label="WORKFLOW" />
                                        )}
                                        {!(formFieldAccess.btnCompletedQA?.hide ?? false) && (
                                        <Tab label="QA" />
                                        )}
                                        {!(formFieldAccess.btnCompletedVariance?.hide ?? false) && (
                                        <Tab label="VARIANCE" />
                                        )}
                                    </Tabs>
                                </Stack>                                
                                <div className='row' style={{ width: '100%', padding: "0px 14px" }}>
                                    <div className='col-12' style={{ borderBottom: '1px solid grey' }}>
                                    </div>
                                </div>
                                <Box display="flex"
                                    flexDirection="column"
                                    sx={{ display: "flex",  }}
                                >
                                    {activeTab === 0 && renderWorkflowForm(true)}
                                    {activeTab === 1 && renderQAForm(true)}
                                    {activeTab === 2 && renderVarianceForm(true)}
                                </Box>
                            </div>
                        </div>
                    </div >
                </div>
            </div>
        </div>
    );

    if (isRestrictedRole) return null;

    return (
        <div>
            {mainCompletedContent}

            {/* Include the Notification Dialog component */}
            <NotificationDialog
                openNotification={notificationDialogOpen}
                dialogContent={notificationDialogContent}
                onCloseNotification={handleCloseNotificationDialog}
                isAutoClose={timerAutoCloseNotificationDialog}
                timerDuration={timerNotificationDialogClose}
            />

            <AlertNotification
                alertType={alertProps.type}
                message={alertProps.message}
                open={alertProps.open}
                onClose={handleAlertClose}
            />

            <Dialog open={exportDialogOpen} onClose={handleExportDialogClose} maxWidth="md" fullWidth>
                <Box sx={{ borderRadius: '8px' }}>
                    <DialogTitle sx={{ m: 0, p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "24px" }}>
                                Export
                            </Typography>
                            <IconButton
                                aria-label="close"
                                onClick={handleExportDialogClose}
                                size="small"
                                sx={{ color: 'text.secondary' }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ py: 3 }}>

                        <fieldset>
                            <legend>
                                <span style={{ fontWeight: 500, fontSize: "18px" }}>Headers</span>
                            </legend>
                            <input
                                type="radio"
                                name="includeHeaders"
                                value="yes"
                                checked={includeHeaders}
                                onChange={() => setIncludeHeaders(true)}
                            />
                            <span style={{ marginLeft: "6px", marginRight: "24px", fontSize: "18px" }}>Include Headers</span>
                            <input
                                type="radio"
                                name="includeHeaders"
                                value="no"
                                checked={!includeHeaders}
                                onChange={() => setIncludeHeaders(false)}
                            />
                            <span style={{ marginLeft: "6px", marginRight: "24px", fontSize: "18px" }}>No Headers</span>
                        </fieldset>

                        <fieldset>
                            <legend>
                                <span style={{ fontWeight: 500, fontSize: "18px" }}>Format</span>
                            </legend>
                            <label>
                                <input type="radio" name="format" value="excel" onChange={handleFormatChange} checked={format === 'excel'} />
                                <span style={{ marginLeft: "6px", marginRight: "116px", fontSize: "18px" }}>Excel</span>
                            </label>
                            <label>
                                <input type="radio" name="format" value="csv" onChange={handleFormatChange} checked={format === 'csv'} />
                                <span style={{ marginLeft: "6px", marginRight: "24px", fontSize: "18px" }}>CSV</span>
                            </label>
                        </fieldset>

                        <fieldset>
                            <legend>
                                <span style={{ fontWeight: 500, fontSize: "18px" }}>Page Selection</span>
                            </legend>
                            <label>
                                <input type="radio" name="pageSelection" value="current" onChange={handlePageSelectionChange} checked={pageSelection === 'current'} />
                                <span style={{ marginLeft: "6px", marginRight: "98px", fontSize: "18px" }}>Current</span>
                            </label>
                            <label>
                                <input type="radio" name="pageSelection" value="all" onChange={handlePageSelectionChange} checked={pageSelection === 'all'} />
                                <span style={{ marginLeft: "6px", marginRight: "36px", fontSize: "18px" }}>All</span>
                            </label>
                        </fieldset>

                        <fieldset>
                            <legend>
                                <span style={{ fontWeight: 500, fontSize: "18px" }}>Filters</span>
                            </legend>
                            <input
                                type="radio"
                                name="includeFilters"
                                value="yes"
                                checked={includeFilters}
                                onChange={() => setIncludeFilters(true)}
                            />
                            <span style={{ marginLeft: "6px", marginRight: "66px", fontSize: "18px" }}>With Filters</span>
                            <input
                                type="radio"
                                name="includeFilters"
                                value="no"
                                checked={!includeFilters}
                                onChange={() => setIncludeFilters(false)}
                            />
                            <span style={{ marginLeft: "6px", marginRight: "24px", fontSize: "18px" }}>Without Filters</span>
                        </fieldset>

                        <fieldset>
                            <legend>
                                <span style={{ fontWeight: 500, fontSize: "18px" }}>File Name {!(fileName || '').trim() && <span style={{ color: 'red' }}>*</span>}</span>
                            </legend>
                        </fieldset>
                        {!(fileName || '').trim() && (
                            <div style={{ color: 'red', fontSize: '14px', marginTop: '-8px', marginBottom: '4px' }}>
                                File Name is required
                            </div>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: -2, flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                name="fileName"
                                onChange={handleFileNameChange}
                                value={fileName}
                                style={{ width: "550px", fontSize: "18px", height: "36px" }}
                            />
                            <Button
                                onClick={handleExportExecute}
                                variant="contained"
                                disabled={!(fileName || '').trim()}
                                sx={{ backgroundColor: '#131E42', fontWeight: 500, ml: 8, mt: '40px', '&:hover': { backgroundColor: '#131E42' } }}
                            >
                                DOWNLOAD
                            </Button>
                            <Button
                                onClick={handleExportDialogClose}
                                variant="contained"
                                sx={{ backgroundColor: '#5A73EA', fontWeight: 500, mt: '40px', '&:hover': { backgroundColor: '#5A73EA' } }}
                            >
                                Cancel
                            </Button>
                        </Box>

                    </DialogContent>
                </Box>
            </Dialog>

            {/* QA Confirmation Dialog */}
            <Dialog
                open={qaConfirmDialogOpen}
                onClose={undefined}
                disableEscapeKeyDown
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#131E42', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Start QA Workflows
                    <IconButton size="small" onClick={handleQAConfirmCancel} sx={{ color: '#131E42' }}>✕</IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        You are about to start {qaCheckedRowIds.size} new QA Workflow(s).
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleQAConfirmCancel} variant="text" sx={{ color: '#5A73EA', fontWeight: 500 }}>
                        CANCEL
                    </Button>
                    <Button onClick={handleQAConfirmOK} variant="contained" sx={{ backgroundColor: '#5A73EA', fontWeight: 500 }}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* QA Success Dialog */}
            <Dialog
                open={qaSuccessDialogOpen}
                onClose={undefined}
                disableEscapeKeyDown
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#131E42', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Workflows Started
                    <IconButton size="small" onClick={handleQASuccessClose} sx={{ color: '#131E42' }}>✕</IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {qaSuccessCount} QA workflow(s) created.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleQASuccessClose} variant="contained" sx={{ backgroundColor: '#5A73EA', fontWeight: 500 }}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Action Taken Details pop-out window */}
            <Dialog
                open={actionTakenDialogOpen}
                onClose={(event, reason) => {
                    if (reason === 'backdropClick') return;
                    handleCloseActionTakenDialog();
                }}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: '97vw',
                        height: '85vh',
                        maxWidth: '2200px',
                        margin: 0,
                        borderRadius: '10px',
                        border: '1px solid #d0d5dd',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                        boxSizing: 'border-box',
                        overflow: 'hidden'
                    }
                }}
            >
                <Box className="action-taken-popup" sx={{ position: 'relative', height: '100%' }}>
                    <IconButton
                        onClick={handleCloseActionTakenDialog}
                        title="Close"
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 1400,
                            backgroundColor: '#fff',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                            '&:hover': { backgroundColor: '#f0f0f0' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {actionTakenDialogOpen && (
                        <ActionTakenDetails row={actionTakenDialogRow} actionTakenData={{}} />
                    )}
                </Box>
            </Dialog>

            {/* Include the loader component */}
            <PageLoader isPageLoading={isPageLoading} />



        </div>
    );
});

export default QueueCompleted;

